/* ==========================================================================
   BIG CHIEF — theme.js
   Vanilla, no dependencies. Every module is defensive: if its markup isn't
   on the page it returns quietly, and nothing here is required for the page
   to be readable or purchasable.
   ========================================================================== */

(function () {
  'use strict';

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  const money = (cents) => {
    const fmt = (window.BigChief && window.BigChief.moneyFormat) || '${{amount}}';
    const amount = (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  };

  /* ------------------------------------------------------------------------
     Focus trap — shared by the cart drawer, mobile menu and age gate.
     ------------------------------------------------------------------------ */

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, onEscape) {
    const handler = (e) => {
      if (e.key === 'Escape') {
        onEscape && onEscape();
        return;
      }
      if (e.key !== 'Tab') return;

      const nodes = $$(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
      if (!nodes.length) return;

      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }

  /* Scroll lock that survives nested overlays */
  let lockCount = 0;
  let savedScroll = 0;

  function lockScroll() {
    if (lockCount === 0) {
      savedScroll = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScroll}px`;
      document.body.style.width = '100%';
    }
    lockCount += 1;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedScroll);
    }
  }

  /* ------------------------------------------------------------------------
     Age gate
     ------------------------------------------------------------------------ */

  function initAgeGate() {
    const gate = $('[data-age-gate]');
    if (!gate) return;

    const COOKIE = 'bc_age_ok';
    const days = parseInt(gate.dataset.cookieDays, 10) || 30;
    const minAge = parseInt(gate.dataset.minAge, 10) || 21;

    /* The theme editor renders the storefront in an iframe on admin.shopify.com,
       so the pass cookie is written in a third-party context and browsers that
       partition or block those (Incognito does by default) never see it again.
       The gate then reappears on every click and makes the theme uneditable.
       It has no compliance value inside the editor, so it stands down there. */
    if (window.Shopify && window.Shopify.designMode) {
      gate.remove();
      document.documentElement.dataset.ageOk = 'true';
      return;
    }

    /* Belt and braces on the storefront too: remember the pass in BOTH a cookie
       and localStorage and accept either. A blocked or cleared cookie alone
       should not put a shopper back behind the gate on the next page. */
    function remembered() {
      if (document.cookie.split('; ').some((c) => c.startsWith(COOKIE + '='))) return true;
      try { return localStorage.getItem(COOKIE) === '1'; } catch (e) { return false; }
    }

    if (remembered()) {
      gate.remove();
      document.documentElement.dataset.ageOk = 'true';
      return;
    }

    gate.hidden = false;
    /* The stylesheet holds the gate at opacity 0 and only reveals it on .is-in.
       Without this line the gate stays invisible while still covering the
       viewport at z-index 200 — so the page looks normal but nothing on it can
       be clicked, and the gate itself can never be dismissed. The static
       preview added this in its own inline script; the theme never did. */
    requestAnimationFrame(function () { gate.classList.add('is-in'); });
    lockScroll();
    const release = trapFocus(gate);

    const choice = $('[data-gate-choice]', gate);
    const denied = $('[data-gate-denied]', gate);
    const dob = $('[data-gate-dob]', gate);
    const yes = $('[data-gate-yes]', gate);

    // Land the keyboard on the primary action, not the container.
    const firstField = dob ? $('[data-dob-month]', dob) : yes;
    if (firstField) firstField.focus();

    function pass() {
      const exp = new Date(Date.now() + days * 864e5).toUTCString();
      document.cookie = `${COOKIE}=1; expires=${exp}; path=/; SameSite=Lax`;
      try { localStorage.setItem(COOKIE, '1'); } catch (e) {}
      release();
      unlockScroll();
      gate.classList.remove('is-in');          // let the .38s fade run
      setTimeout(function () { gate.remove(); }, 420);
      document.documentElement.dataset.ageOk = 'true';
      document.dispatchEvent(new CustomEvent('bc:age-verified'));
    }

    yes && yes.addEventListener('click', pass);

    const no = $('[data-gate-no]', gate);
    no &&
      no.addEventListener('click', () => {
        if (choice) choice.hidden = true;
        if (dob) dob.hidden = true;
        denied.hidden = false;
        const back = $('[data-gate-back]', gate);
        back && back.focus();
      });

    const back = $('[data-gate-back]', gate);
    back &&
      back.addEventListener('click', () => {
        denied.hidden = true;
        if (choice) choice.hidden = false;
        if (dob) dob.hidden = false;
        (firstField || yes).focus();
      });

    // Optional month/year check
    if (dob) {
      const monthEl = $('[data-dob-month]', dob);
      const yearEl = $('[data-dob-year]', dob);
      const errEl = $('[data-dob-error]', dob);

      // Auto-advance so two fields still feel like one entry.
      monthEl.addEventListener('input', () => {
        monthEl.value = monthEl.value.replace(/\D/g, '');
        if (monthEl.value.length === 2) yearEl.focus();
      });
      yearEl.addEventListener('input', () => {
        yearEl.value = yearEl.value.replace(/\D/g, '');
      });

      dob.addEventListener('submit', (e) => {
        e.preventDefault();
        const m = parseInt(monthEl.value, 10);
        const y = parseInt(yearEl.value, 10);
        const now = new Date();

        if (!m || m < 1 || m > 12 || !y || y < 1900 || y > now.getFullYear()) {
          errEl.textContent = gate.dataset.invalidText || 'Enter a valid month and year.';
          errEl.hidden = false;
          return;
        }

        let age = now.getFullYear() - y;
        if (now.getMonth() + 1 < m) age -= 1;

        if (age < minAge) {
          if (choice) choice.hidden = true;
          dob.hidden = true;
          denied.hidden = false;
          return;
        }
        pass();
      });
    }
  }

  /* ------------------------------------------------------------------------
     Generic drawer (cart + mobile menu)
     ------------------------------------------------------------------------ */

  function initDrawer(drawer, openSelector, closeSelector) {
    if (!drawer) return null;

    let release = null;
    let lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      drawer.hidden = false;
      // Next frame so the transition actually runs from its start state.
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      lockScroll();
      release = trapFocus(drawer, close);

      const target = $(FOCUSABLE, $('.drawer__panel', drawer));
      if (target) target.focus();

      $$(openSelector).forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    }

    function close() {
      drawer.classList.remove('is-open');
      release && release();
      unlockScroll();
      $$(openSelector).forEach((btn) => btn.setAttribute('aria-expanded', 'false'));

      const done = () => {
        drawer.hidden = true;
        drawer.removeEventListener('transitionend', done);
      };
      // Fall back to a timeout in case the transition is suppressed.
      drawer.addEventListener('transitionend', done);
      setTimeout(done, 600);

      if (lastFocus) lastFocus.focus();
    }

    $$(openSelector).forEach((btn) => btn.addEventListener('click', open));
    $$(closeSelector, drawer).forEach((btn) => btn.addEventListener('click', close));

    return { open, close };
  }

  /* ------------------------------------------------------------------------
     Cart
     ------------------------------------------------------------------------ */

  const Cart = {
    drawer: null,

    async fetchState() {
      const res = await fetch(`${window.BigChief.routes.cart}.js`, {
        headers: { Accept: 'application/json' },
      });
      return res.json();
    },

    async add(formData) {
      const res = await fetch(window.BigChief.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add to cart');
      }
      return res.json();
    },

    async change(line, quantity) {
      const res = await fetch(window.BigChief.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      return res.json();
    },

    render(state) {
      // Badge
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = state.item_count;
        el.hidden = state.item_count === 0;
      });

      const body = $('[data-cart-body]');
      const foot = $('[data-cart-foot]');
      if (!body) return;

      if (state.item_count === 0) {
        body.innerHTML = `<p class="lede" style="font-size:var(--fs-base);">${
          body.dataset.emptyText || 'Your cart is empty.'
        }</p>`;
        if (foot) foot.hidden = true;
        return;
      }

      if (foot) foot.hidden = false;

      body.innerHTML = state.items
        .map((item, i) => {
          const img = item.image
            ? `<img src="${item.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2')}" alt="" loading="lazy">`
            : '';
          const opts =
            item.options_with_values && item.options_with_values.length
              ? item.options_with_values
                  .filter((o) => o.value && o.value !== 'Default Title')
                  .map((o) => o.value)
                  .join(' · ')
              : '';

          return `
            <div class="line" data-line="${i + 1}">
              <div class="line__media">${img}</div>
              <div class="line__body">
                <a href="${item.url}" class="line__title">${item.product_title}</a>
                ${opts ? `<span class="line__opt">${opts}</span>` : ''}
                <div class="cluster" style="justify-content:space-between;">
                  <div class="qty">
                    <button type="button" data-qty-down aria-label="Decrease quantity">&minus;</button>
                    <input type="number" value="${item.quantity}" min="0" data-qty-input aria-label="Quantity">
                    <button type="button" data-qty-up aria-label="Increase quantity">+</button>
                  </div>
                  <span class="price">${money(item.final_line_price)}</span>
                </div>
              </div>
            </div>`;
        })
        .join('');

      const total = $('[data-cart-total]');
      if (total) total.textContent = money(state.total_price);

      this.renderProgress(state);
    },

    renderProgress(state) {
      const wrap = $('[data-progress]');
      if (!wrap) return;

      const threshold = window.BigChief.freeShippingThreshold;
      if (!threshold) {
        wrap.hidden = true;
        return;
      }

      wrap.hidden = false;
      const remaining = Math.max(0, threshold - state.total_price);
      const pct = Math.min(100, (state.total_price / threshold) * 100);

      const fill = $('[data-progress-fill]', wrap);
      const label = $('[data-progress-label]', wrap);

      if (fill) fill.style.width = `${pct}%`;
      if (label) {
        label.textContent =
          remaining > 0
            ? `${money(remaining)} away from free shipping`
            : 'Free shipping unlocked';
      }
    },

    async refresh() {
      const state = await this.fetchState();
      this.render(state);
      return state;
    },
  };

  function initCart() {
    const drawer = $('[data-cart-drawer]');
    Cart.drawer = initDrawer(drawer, '[data-cart-open]', '[data-cart-close]');

    if (!drawer) return;

    // Quantity controls, delegated so re-rendered lines keep working.
    drawer.addEventListener('click', async (e) => {
      const up = e.target.closest('[data-qty-up]');
      const down = e.target.closest('[data-qty-down]');
      if (!up && !down) return;

      const row = e.target.closest('[data-line]');
      const input = $('[data-qty-input]', row);
      const next = parseInt(input.value, 10) + (up ? 1 : -1);

      const state = await Cart.change(parseInt(row.dataset.line, 10), Math.max(0, next));
      Cart.render(state);
    });

    drawer.addEventListener('change', async (e) => {
      const input = e.target.closest('[data-qty-input]');
      if (!input) return;
      const row = input.closest('[data-line]');
      const state = await Cart.change(parseInt(row.dataset.line, 10), Math.max(0, parseInt(input.value, 10) || 0));
      Cart.render(state);
    });

    Cart.refresh();
  }

  /* ------------------------------------------------------------------------
     Product form
     ------------------------------------------------------------------------ */

  function initProductForm() {
    const form = $('#product-form');
    if (!form) return;

    const dataEl = $('[data-variant-data]');
    const variants = dataEl ? JSON.parse(dataEl.textContent) : [];

    const idInput = $('[data-variant-id]', form);
    const addBtn = $('[data-add-button]', form);
    const addText = $('[data-add-text]', form);
    const priceEl = $('[data-price]');
    const errorEl = $('[data-form-error]', form);

    function selectedOptions() {
      const inputs = $$('[data-option-input]:checked', form);
      return inputs
        .sort((a, b) => a.dataset.position - b.dataset.position)
        .map((i) => i.value);
    }

    function matchVariant(opts) {
      return variants.find((v) => v.options.every((o, i) => o === opts[i]));
    }

    function update() {
      const opts = selectedOptions();
      const variant = matchVariant(opts);

      // Keep the visible "current value" labels in sync.
      $$('[data-option-input]:checked', form).forEach((input) => {
        const field = input.closest('[data-option-index]');
        if (!field) return;
        const label = $(`[data-option-current="${field.dataset.optionIndex}"]`, form);
        if (label) label.textContent = input.value;
      });

      if (!variant) {
        addBtn.disabled = true;
        addText.textContent = window.BigChief.strings.unavailable;
        return;
      }

      idInput.value = variant.id;

      // Reflect the choice in the URL so the variant is shareable and
      // survives a refresh.
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      if (priceEl) {
        const sale = variant.compare_at_price > variant.price;
        priceEl.innerHTML = `<span class="price ${sale ? 'price--sale' : ''}">${
          sale ? `<s class="price__was">${money(variant.compare_at_price)}</s>` : ''
        }${money(variant.price)}</span>`;
      }

      addBtn.disabled = !variant.available;
      addText.textContent = variant.available
        ? window.BigChief.strings.addToCart
        : window.BigChief.strings.soldOut;

      // Swap the gallery to the variant's image when it has its own.
      if (variant.featured_image) {
        const main = $('[data-gallery-main]');
        if (main) {
          main.src = variant.featured_image.src;
          main.srcset = '';
        }
      }
    }

    form.addEventListener('change', (e) => {
      if (e.target.matches('[data-option-input]')) update();
    });

    form.addEventListener('submit', async (e) => {
      // If the bundle app is driving this submit, stay out of its way.
      if (form.dataset.bundleActive === 'true') return;

      e.preventDefault();
      if (errorEl) errorEl.hidden = true;

      addBtn.disabled = true;
      const original = addText.textContent;
      addText.textContent = '…';

      try {
        await Cart.add(new FormData(form));
        await Cart.refresh();
        buzz([10, 40, 18]);
        Cart.drawer && Cart.drawer.open();
        addText.textContent = original;
      } catch (err) {
        if (errorEl) {
          errorEl.textContent = err.message;
          errorEl.hidden = false;
          errorEl.classList.add('form-note--error');
        }
        addText.textContent = original;
      } finally {
        addBtn.disabled = false;
      }
    });

    update();
  }

  /* ------------------------------------------------------------------------
     Gallery
     ------------------------------------------------------------------------ */

  function initGallery() {
    const gallery = $('[data-gallery]');
    if (!gallery) return;

    const main = $('[data-gallery-main]', gallery);
    if (!main) return;

    $$('[data-gallery-thumb]', gallery).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        main.src = thumb.dataset.src;
        main.srcset = '';
        main.alt = thumb.dataset.alt || '';
        $$('[data-gallery-thumb]', gallery).forEach((t) =>
          t.setAttribute('aria-current', t === thumb ? 'true' : 'false')
        );
      });
    });
  }

  /* ------------------------------------------------------------------------
     Scroll reveal
     ------------------------------------------------------------------------ */

  function initReveal() {
    const nodes = $$('[data-reveal]');
    if (!nodes.length) return;

    // No IntersectionObserver, or reduced motion: show everything immediately.
    if (
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      nodes.forEach((n) => n.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    nodes.forEach((n) => io.observe(n));

    /* Same fail-safe as initRise: instant jumps can skip the observer and
       leave cards faded forever. Sweep on scroll, and reveal everything
       outright five seconds in — content is never allowed to stay dim. */
    function sweep() {
      nodes.forEach((n) => {
        if (n.classList.contains('is-in')) return;
        const r = n.getBoundingClientRect();
        if (r.top < window.innerHeight || r.bottom <= 0) { n.classList.add('is-in'); io.unobserve(n); }
      });
    }
    let ticking = false;
    document.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { ticking = false; sweep(); });
    }, { passive: true, capture: true });
    setTimeout(sweep, 600);
    setTimeout(() => { nodes.forEach((n) => n.classList.add('is-in')); io.disconnect(); }, 5000);
  }

  /* ------------------------------------------------------------------------
     Header hide-on-scroll
     ------------------------------------------------------------------------ */

  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;

    let last = window.scrollY;
    let ticking = false;

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;

        requestAnimationFrame(() => {
          const y = window.scrollY;
          // Never hide near the top, and ignore sub-pixel jitter.
          const hide = y > 220 && y > last + 4;
          if (y < last - 4 || y <= 220) header.dataset.hidden = 'false';
          else if (hide) header.dataset.hidden = 'true';

          last = y;
          ticking = false;
        });
      },
      { passive: true }
    );
  }


  /* ------------------------------------------------------------------------
     Verify video
     ------------------------------------------------------------------------ */

  function initVerifyVideo() {
    const video = $('[data-verify-video]');
    if (!video) return;

    const toggle = $('[data-verify-toggle]');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function setState(playing) {
      if (toggle) {
        toggle.dataset.playing = playing ? 'true' : 'false';
        toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      }
    }

    toggle &&
      toggle.addEventListener('click', () => {
        if (video.paused) {
          video.play().then(() => setState(true)).catch(() => setState(false));
        } else {
          video.pause();
          setState(false);
        }
      });

    video.addEventListener('play', () => setState(true));
    video.addEventListener('pause', () => setState(false));

    // Autoplay only when scrolled into view, and never under reduced motion.
    // preload is "none", so nothing downloads until the user is close to it.
    if (reduce || video.dataset.autoplay !== 'true' || !('IntersectionObserver' in window)) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().then(() => setState(true)).catch(() => {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.45 }
    );

    io.observe(video);
  }


  /* ------------------------------------------------------------------------
     Quick add (product cards)
     ------------------------------------------------------------------------ */

  function initQuickAdd() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;

      e.preventDefault();
      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = '\u2026';

      try {
        const fd = new FormData();
        fd.append('id', btn.dataset.variantId);
        fd.append('quantity', 1);
        await Cart.add(fd);
        await Cart.refresh();
        buzz([10, 40, 18]);
        btn.textContent = '\u2713';
        Cart.drawer && Cart.drawer.open();
      } catch (err) {
        btn.textContent = original;
      } finally {
        setTimeout(() => {
          btn.disabled = false;
          btn.textContent = original;
        }, 1200);
      }
    });
  }


  /* ------------------------------------------------------------------------
     Email / SMS capture
     ------------------------------------------------------------------------ */

  function initCapture() {
    const el = $('[data-capture]');
    if (!el) return;

    const COOKIE = 'bc_capture';
    const days = parseInt(el.dataset.days, 10) || 30;
    if (document.cookie.split('; ').some((c) => c.startsWith(COOKIE + '='))) return;

    const minDelay = parseInt(el.dataset.delay, 10) || 12000;
    const scrollAt = parseInt(el.dataset.scroll, 10) || 35;
    const useExit = el.dataset.exitIntent === 'true';

    let armed = false;
    let done = false;
    let release = null;

    setTimeout(() => { armed = true; }, minDelay);

    function suppress() {
      const exp = new Date(Date.now() + days * 864e5).toUTCString();
      document.cookie = `${COOKIE}=1; expires=${exp}; path=/; SameSite=Lax`;
    }

    function open() {
      // Never stack on top of the age gate.
      if (done || !armed || $('[data-age-gate]')) return;
      done = true;
      el.hidden = false;
      requestAnimationFrame(() => el.classList.add('is-open'));
      lockScroll();
      release = trapFocus(el, close);
      const field = $('[data-capture-email]', el);
      if (field) field.focus();
      teardown();
    }

    function close() {
      el.classList.remove('is-open');
      release && release();
      unlockScroll();
      suppress();
      setTimeout(() => { el.hidden = true; }, 300);
    }

    function onExit(e) { if (e.clientY <= 0) open(); }
    function onScroll() {
      const pct = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      if (pct >= scrollAt) open();
    }
    function teardown() {
      document.removeEventListener('mouseout', onExit);
      window.removeEventListener('scroll', onScroll);
    }

    if (useExit && window.matchMedia('(hover: hover)').matches) {
      document.addEventListener('mouseout', onExit);
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    $$('[data-capture-close]', el).forEach((b) => b.addEventListener('click', close));

    function step(name) {
      $$('[data-capture-step]', el).forEach((s) => { s.hidden = s.dataset.captureStep !== name; });
    }

    // Step 1 -> step 2. The email is banked before a phone number is ever asked
    // for, so an abandon at step 2 still leaves a usable lead.
    const emailStep = $('[data-capture-step="email"]', el);
    const emailForm = emailStep && emailStep.querySelector('form');
    emailForm &&
      emailForm.addEventListener('submit', () => {
        const val = $('[data-capture-email]', el);
        const mirror = $('[data-capture-email-mirror]', el);
        if (mirror && val) mirror.value = val.value;
        suppress();
        const smsStep = $('[data-capture-step="sms"]', el);
        setTimeout(() => step(smsStep ? 'sms' : 'done'), 60);
      });

    const smsStep = $('[data-capture-step="sms"]', el);
    const smsForm = smsStep && smsStep.querySelector('form');
    smsForm && smsForm.addEventListener('submit', () => setTimeout(() => step('done'), 60));

    const code = $('[data-capture-code]', el);
    code &&
      code.addEventListener('click', () => {
        navigator.clipboard && navigator.clipboard.writeText(code.dataset.captureCode);
        const small = code.querySelector('small');
        if (small) {
          const was = small.textContent;
          small.textContent = 'Copied';
          setTimeout(() => { small.textContent = was; }, 1600);
        }
      });

    // If the age gate is up, wait for it to clear before arming.
    if ($('[data-age-gate]')) {
      armed = false;
      document.addEventListener('bc:age-verified', () => {
        setTimeout(() => { armed = true; }, minDelay);
      });
    }
  }

  /* ------------------------------------------------------------------------
     Sticky add-to-cart (mobile PDP)
     ------------------------------------------------------------------------ */

  function initStickyAtc() {
    const bar = $('[data-sticky-atc]');
    const form = $('#product-form');
    if (!bar || !form) return;

    const anchor = $('[data-add-button]', form);
    if (!anchor) return;

    // Show the bar only once the real button has scrolled out of view.
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => bar.classList.toggle('is-visible', !e.isIntersecting));
      },
      { rootMargin: '0px 0px -80px 0px' }
    );
    io.observe(anchor);

    const btn = $('[data-sticky-add]', bar);
    btn && btn.addEventListener('click', () => anchor.click());
  }


  /* ------------------------------------------------------------------------
     Haptics — magnetic buttons, cursor tilt, scroll drift, device vibration.
     All of it is additive: if any of it fails the UI still works exactly the
     same, just less physical.
     ------------------------------------------------------------------------ */

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  // Short pulse on meaningful commits (add to cart). No-op where unsupported.
  function buzz(pattern) {
    if (REDUCED) return;
    try { navigator.vibrate && navigator.vibrate(pattern || 12); } catch (e) {}
  }

  function initMagnetic() {
    if (REDUCED || !FINE_POINTER) return;

    // Primary calls to action lean toward the cursor as it approaches.
    // Note 94 took the magnet off the hero CTAs because a button that leans
    // toward the cursor — on a 420ms spring that overshoots before settling —
    // drifts under the pointer as you go to click it. That reasoning was never
    // extended to the rest of the primary buttons, so "Shop disposables" and
    // its neighbours kept chasing. Six of them shared one section listener,
    // each running getBoundingClientRect on every single mousemove.
    // Magnetism now stays on the decorative controls only; commerce buttons
    // sit still and get a plain hover lift instead.
    $$('.hero-carousel__arrow, .chart__go').forEach((el) => {
      el.setAttribute('data-magnetic', '');
      const strength = el.classList.contains('btn--lg') ? 0.22 : 0.3;
      const radius = 90;

      let queued = false, atRest = true, lastEvent = null;

      function onMove(e) {
        lastEvent = e;
        if (queued) return;            // one measurement per frame, not per event
        queued = true;
        requestAnimationFrame(apply);
      }
      function apply() {
        queued = false;
        const e = lastEvent;
        if (!e) return;
        const r = el.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        if (Math.hypot(dx, dy) > Math.max(r.width, r.height) / 2 + radius) return reset();
        el.style.setProperty('--mx', `${dx * strength}px`);
        el.style.setProperty('--my', `${dy * strength}px`);
        atRest = false;
      }
      function reset() {
        if (atRest) return;            // don't rewrite zeros every frame
        atRest = true;
        el.style.setProperty('--mx', '0px');
        el.style.setProperty('--my', '0px');
      }

      const parent = el.closest('section, header, .hero-carousel') || document;
      parent.addEventListener('mousemove', onMove);
      parent.addEventListener('mouseleave', reset);
      el.addEventListener('click', () => { buzz(14); reset(); });
    });
  }

  /* --------------------------------------------------------------------
     HERO CAROUSEL
     sections/hero.liquid ships the tabs, slides and data-interval, but the
     behaviour only ever existed as an inline script in the static preview —
     so on Shopify the hero tabs rendered and did nothing. Ported here, where
     the theme can actually reach it. Hooks are unchanged from the preview.
     -------------------------------------------------------------------- */
  function initHero() {
    const root = $('[data-hero]');
    if (!root) return;

    const S = $$('[data-hero-slide]', root);
    const D = $$('[data-hero-dot]', root);
    if (!S.length) return;

    const iv = parseInt(root.dataset.interval, 10) || 7000;
    let i = 0, t = null;
    root.style.setProperty('--interval', iv + 'ms');

    function show(n) {
      i = (n + S.length) % S.length;
      S.forEach((sl, x) => {
        sl.toggleAttribute('data-active', x === i);
        if (x === i) sl.removeAttribute('aria-hidden');
        else sl.setAttribute('aria-hidden', 'true');
      });
      D.forEach((d, x) => {
        d.setAttribute('aria-selected', x === i ? 'true' : 'false');
        d.classList.toggle('is-on', x === i);
        const f = $('.htab__fill', d);
        if (f) { f.style.transition = 'none'; f.style.width = '0'; void f.offsetWidth; f.style.transition = ''; }
        if (x === i && f) { d.style.setProperty('--dwell', iv + 'ms'); void f.offsetWidth; f.style.width = '100%'; }
      });
    }
    function start() { if (REDUCED) return; stop(); t = setInterval(() => show(i + 1), iv); }
    function stop() { if (t) { clearInterval(t); t = null; } }

    D.forEach((d) => d.addEventListener('click', () => { show(+d.dataset.index); start(); }));
    const p = $('[data-hero-prev]', root), n = $('[data-hero-next]', root);
    if (p) p.addEventListener('click', () => { show(i - 1); start(); });
    if (n) n.addEventListener('click', () => { show(i + 1); start(); });

    root.addEventListener('mouseenter', () => { root.dataset.paused = 'true';  stop();  });
    root.addEventListener('mouseleave', () => { root.dataset.paused = 'false'; start(); });
    root.addEventListener('focusin',    () => { root.dataset.paused = 'true';  stop();  });
    root.addEventListener('focusout',   () => { root.dataset.paused = 'false'; start(); });
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));

    // no visible arrows, so keyboard and swipe carry the navigation
    root.setAttribute('tabindex', '0');
    root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); show(i - 1); start(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); show(i + 1); start(); }
    });
    let x0 = null, y0 = null;
    root.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
    root.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy)) { show(dx < 0 ? i + 1 : i - 1); start(); }
      x0 = y0 = null;
    }, { passive: true });

    show(0);
    // data-single = the Duo carries the hero alone: no autoplay, slides stay in the DOM
    if (!root.hasAttribute('data-single')) start();
  }

  function initTilt() {
    if (REDUCED || !FINE_POINTER) return;

    $$('.diecut__link').forEach((el) => {
      const art = $('.diecut__img', el);
      if (!art) return;
      art.setAttribute('data-tilt', '');

      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        art.style.setProperty('--ry', `${px * 14}deg`);
        art.style.setProperty('--rx', `${-py * 10}deg`);
      });
      el.addEventListener('mouseleave', () => {
        art.style.setProperty('--ry', '0deg');
        art.style.setProperty('--rx', '0deg');
      });
    });
  }

  function initDrift() {
    if (REDUCED) return;
    const arts = $$('.hero-slide__cutout');
    if (!arts.length) return;

    let ticking = false;
    function update() {
      const y = window.scrollY;
      // Gentle counter-scroll so the product feels anchored in space rather
      // than glued to the page.
      arts.forEach((a) => a.style.setProperty('--drift', `${Math.min(y * 0.12, 90)}px`));
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
  }


  /* ------------------------------------------------------------------------
     Bundle tiers
     ------------------------------------------------------------------------ */

  function initBundles() {
    const wrap = $('[data-bundles]');
    if (!wrap) return;

    const tiers = $$('[data-bundle-tier]', wrap);
    const nextEl = $('[data-bundle-next]', wrap);
    const form = $('#product-form');
    if (!tiers.length) return;

    const qtyOf = (t) => parseInt(t.dataset.qty, 10) || 1;
    const discOf = (t) => parseInt(t.dataset.discount, 10) || 0;

    function sync() {
      const checked = tiers.find((t) => $('input', t).checked) || tiers[0];

      // :has() fallback for older engines
      tiers.forEach((t) => t.classList.toggle('is-checked', t === checked));

      // Tell the product form how many to add, and let the theme's own submit
      // handler post the right quantity.
      if (form) {
        let q = form.querySelector('input[name="quantity"]');
        if (!q) {
          q = document.createElement('input');
          q.type = 'hidden';
          q.name = 'quantity';
          form.appendChild(q);
        }
        q.value = qtyOf(checked);
      }

      // Distance to the next rung — turns the ladder into something to climb.
      const better = tiers
        .filter((t) => qtyOf(t) > qtyOf(checked) && discOf(t) > discOf(checked))
        .sort((a, b) => qtyOf(a) - qtyOf(b))[0];

      if (better && nextEl) {
        const n = qtyOf(better) - qtyOf(checked);
        nextEl.textContent = `Add ${n} more to save ${discOf(better)}%`;
        nextEl.hidden = false;
      } else if (nextEl) {
        nextEl.hidden = true;
      }
    }

    wrap.addEventListener('change', (e) => {
      if (e.target.matches('input[name="bundle-tier"]')) {
        sync();
        buzz(10);
      }
    });

    // Keyboard: arrow keys move between tiers like a real radio group
    wrap.addEventListener('keydown', (e) => {
      if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      const idx = tiers.findIndex((t) => $('input', t).checked);
      const dir = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
      const next = tiers[(idx + dir + tiers.length) % tiers.length];
      if (next) {
        e.preventDefault();
        $('input', next).checked = true;
        $('input', next).focus();
        sync();
      }
    });

    sync();
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */

  function boot() {
    initAgeGate();
    initHeader();
    initCart();
    initDrawer($('[data-menu]'), '[data-menu-open]', '[data-menu-close]');
    initProductForm();
    initGallery();
    initHero();
    initReveal();
    initVerifyVideo();
    initQuickAdd();
    initCapture();
    initStickyAtc();
    initMagnetic();
    initTilt();
    initDrift();
    initBundles();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run per-section init when the theme editor swaps a section.
  document.addEventListener('shopify:section:load', () => {
    initProductForm();
    initGallery();
    initHero();
    initReveal();
    initVerifyVideo();
    initMagnetic();
    initTilt();
  });

  window.BigChiefCart = Cart;
})();

/* ==========================================================================
   Ship clock, headline auto-fit, flavour picker
   Ported from the preview. Each is defensive: if its markup is not on the
   page the initialiser returns immediately, so every section stays optional.
   ========================================================================== */

/* --------------------------------------------------------------------------
   One clock drives every same-day-shipping element on the page — the hero
   line, the chip on each product card, the product page and the band. Cutoff
   is 5pm PACIFIC, Monday to Friday — warehouse time, not the visitor's clock,
   so a customer in New York sees the same deadline the dock actually keeps.
   Past cutoff (or at a weekend) the promise rolls forward and the copy says
   so, rather than counting down to a deadline that has already gone.
   -------------------------------------------------------------------------- */
function initShipClock() {
  var CUT = 17;
  var any = document.querySelector('[data-ship-text], [data-ship-clock], [data-ship-chip]');
  if (!any) return;

  var DAY = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  /* the current wall-clock in Los Angeles, as a Date we can do day math on */
  function laNow() {
    try {
      var o = {};
      new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles', hour12: false,
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', second: 'numeric'
      }).formatToParts(new Date()).forEach(function (p) { o[p.type] = p.value; });
      return new Date(o.year, o.month - 1, o.day, o.hour % 24, o.minute, o.second);
    } catch (e) { return new Date(); }
  }

  function nextCutoff(now) {
    var d = new Date(now);
    d.setHours(CUT, 0, 0, 0);
    while (d <= now || d.getDay() === 0 || d.getDay() === 6) {
      if (d > now && d.getDay() !== 0 && d.getDay() !== 6) break;
      d.setDate(d.getDate() + 1);
      d.setHours(CUT, 0, 0, 0);
    }
    return d;
  }

  function fmt(ms) {
    var h = Math.floor(ms / 36e5),
        m = Math.floor((ms % 36e5) / 6e4),
        s = Math.floor((ms % 6e4) / 1e3);
    return h > 0 ? h + 'h ' + String(m).padStart(2, '0') + 'm'
                 : m + 'm ' + String(s).padStart(2, '0') + 's';
  }

  function tick() {
    var now = laNow(), cut = nextCutoff(now);

    /* Which day the order actually leaves on, stated plainly. A countdown is
       only useful when the deadline is today — "43h 56m" on a Saturday tells
       nobody anything, and calling Monday "tomorrow" is simply wrong. So:
         same day  -> count down, it is a real deadline
         tomorrow  -> say tomorrow
         further   -> name the day. */
    var days = Math.round((new Date(cut).setHours(0,0,0,0) - new Date(now).setHours(0,0,0,0)) / 864e5);
    var left = cut - now, txt = fmt(left);
    var urgent = days === 0 && left < 90 * 6e4;
    var dayName = DAY[cut.getDay()];

    var line, chip, kicker, band;
    if (days === 0) {
      line   = 'Order within <b data-ship-cutoff>' + txt + '</b> and it ships today';
      chip   = 'Ships today';
      kicker = 'Order in the next';
      band   = 'and your order leaves our California facility <b>today</b>. ';
    } else if (days === 1) {
      /* After the cutoff on a weekday there is no deadline left tonight, so a
         countdown is just a clock with nothing riding on it. Say when it
         actually leaves instead. At midnight this rolls over to the "today"
         branch on its own. */
      line   = 'Ships <b>first thing tomorrow morning</b>';
      chip   = 'Ships tomorrow AM';
      kicker = 'Next one out';
      band   = 'and your order goes out <b>first thing tomorrow morning</b>. ';
    } else {
      line   = 'Ships <b>' + dayName + '</b> &mdash; order now and you go out first';
      chip   = 'Ships ' + dayName;
      kicker = 'Next one out';
      band   = 'and your order is <b>first out ' + dayName + '</b>. ';
    }

    document.querySelectorAll('[data-ship-clock]').forEach(function (el) {
      el.textContent = days === 0 ? txt : dayName;
    });
    document.querySelectorAll('[data-ship-text]').forEach(function (el) { el.innerHTML = line; });
    document.querySelectorAll('[data-ship-chip-txt]').forEach(function (el) { el.textContent = chip; });
    document.querySelectorAll('[data-ship-band-k]').forEach(function (el) { el.textContent = kicker; });
    document.querySelectorAll('[data-ship-band-r]').forEach(function (el) {
      el.innerHTML = band + 'Free shipping over $100 &middot; discreet packaging &middot; tracking the moment it moves.';
    });
    document.querySelectorAll('.shipnote, .shipband, .shipchip').forEach(function (el) {
      el.classList.toggle('is-next', days !== 0);
      el.classList.toggle('is-urgent', urgent);
    });
  }

  tick();
  setInterval(tick, 1000);
}

function initHeadlineFit() {
  // Any headline whose lines are set nowrap needs a size at which they fit.
  // Hero and spotlight both qualify; each measures against ITS OWN column, so
  // one long line in one block never shrinks the others.
  var SEL = '.hero-slide__title, .spot__h, .pdp-hero__h';
  var titles = document.querySelectorAll(SEL);
  if (!titles.length) return;

  function boxFor(h) {
    var col = h.closest('.hero-slide__copy, .spot__copy, .pdp-hero__copy');
    if (col && col.getBoundingClientRect().width) return col.getBoundingClientRect().width;
    // an inactive slide has no width of its own — borrow the live one
    var live = document.querySelector('.hero-slide[data-active] .hero-slide__copy');
    return live ? live.getBoundingClientRect().width : 0;
  }

  function fit() {
    /* The auto-fit measures each line with white-space:nowrap and shrinks the
       type until the longest fits its column — right on a wide screen, where a
       headline line is meant to hold together.

       Below 62em the headline is allowed to wrap, so that measurement is of a
       width the line never occupies. And because --fit is written as an inline
       style it beats whatever size the mobile CSS sets. Stand down there and
       let wrapping do the work. */
    var narrow = window.matchMedia('(max-width: 61.99em)').matches;

    titles.forEach(function (h) {
      h.style.setProperty('--fit', 1);
      if (narrow) return;
      var box = boxFor(h);
      if (!box) return;

      var cs = getComputedStyle(h);
      var probe = document.createElement('span');
      probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;left:-9999px;top:0';
      probe.style.font = cs.fontWeight + ' ' + cs.fontSize + '/1 ' + cs.fontFamily;
      probe.style.letterSpacing = cs.letterSpacing;
      probe.style.textTransform = cs.textTransform;
      document.body.appendChild(probe);

      var widest = 0;
      var lines = h.querySelectorAll('.hline');
      (lines.length ? lines : [h]).forEach(function (line) {
        // a line span may itself contain a <br>; measuring the pair as one
        // string would shrink the headline to fit a width it never needs
        line.innerHTML.split(/<br\s*\/?>/i).forEach(function (part) {
          probe.innerHTML = part;
          widest = Math.max(widest, probe.getBoundingClientRect().width);
        });
      });
      probe.remove();
      h.style.setProperty('--fit', widest > box ? Math.max(0.55, box / widest).toFixed(3) : 1);
    });
  }

  window.__heroFit = fit;
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit); else fit();
  window.addEventListener('resize', fit, { passive: true });
}

/* --------------------------------------------------------------------------
   Flavour picker. Choosing a pairing retints the whole stage in that pairing's
   two strain colours, so the choice is something you watch happen to the
   product rather than a label swap.
   -------------------------------------------------------------------------- */
function initFlavourPicker() {
  var beads = Array.prototype.slice.call(document.querySelectorAll('[data-pair]'));
  if (!beads.length) return;
  var panels = Array.prototype.slice.call(document.querySelectorAll('[data-panel]'));
  var wash = document.querySelector('.pdp-hero__wash');
  var pool = document.querySelector('.pdp-hero__pool');

  function pick(i) {
    beads.forEach(function (b) {
      var on = Number(b.dataset.pair) === i;
      b.classList.toggle('is-on', on);
      if (b.hasAttribute('aria-selected')) b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    panels.forEach(function (p) { p.classList.toggle('is-on', Number(p.dataset.panel) === i); });

    var src = beads.filter(function (b) { return Number(b.dataset.pair) === i; })[0];
    if (!src) return;
    var cs = getComputedStyle(src);
    var a = cs.getPropertyValue('--a').trim(), b2 = cs.getPropertyValue('--b').trim();
    [wash, pool].forEach(function (el) {
      if (!el) return;
      el.style.setProperty('--a', a);
      el.style.setProperty('--b', b2);
    });
  }

  beads.forEach(function (b) {
    b.addEventListener('click', function () { pick(Number(b.dataset.pair)); });
  });
  if (panels.length) pick(0);
}

(function(){var go=function(){
  initShipClock();
  initHeadlineFit();
  initFlavourPicker();
};if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',go);else go();})();

/* --------------------------------------------------------------------------
   Recent-order notifications.

   Location without an IP lookup: Intl.DateTimeFormat().resolvedOptions()
   .timeZone gives "America/Los_Angeles" with no network call, no API key, no
   permission prompt and nothing an ad blocker can intercept. Mapped to a pool
   of real towns in that metro — deliberately the small specific ones, because
   a named suburb reads as a person and a major city reads as a plugin.

   Real orders are preferred: point the section at a feed and every line is a
   true statement. Simulated mode exists because it was asked for, but it
   fabricates purchase claims, which is deceptive advertising in the US — the
   section schema says so plainly next to the setting.
   -------------------------------------------------------------------------- */
function initSocialProof() {
  var el = document.querySelector('[data-social-proof]');
  if (!el) return;

  var mode = el.dataset.mode || 'feed';
  if (mode === 'off') return;

  /* Reduced motion means REDUCE MOTION, not remove the notification. Bailing
     out here silently switched the whole feature off for anyone with the macOS
     accessibility setting on — they saw nothing and there was no way to tell
     why. The card still appears; it just arrives without the slide. */
  var calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (calm) el.classList.add('sproof--calm');
  // one run per visit, not per pageview — repeat popups on every navigation is
  // what makes these read as spam
  /* Not "once per session, forever" — that flag survives reloads, so anyone who
     saw the run once never saw it again no matter how many times they came
     back, and there was no way to tell the feature from a broken one. A
     timestamp with a cooling-off window keeps it from spamming a single
     browsing session while letting it return on a later visit. */
  var COOL = 20 * 60 * 1000;
  try {
    var seenAt = parseInt(sessionStorage.getItem('bc_sp_at') || '0', 10);
    if (seenAt && (Date.now() - seenAt) < COOL) return;
  } catch (e) {}

  /* Real towns, grouped by the timezone that covers them. Chosen for
     specificity: somebody in the Inland Empire recognises Rialto. */
  var TOWNS = {
    'America/Los_Angeles': ['Rancho Cucamonga','Rialto','Fontana','Chino Hills','Covina','Norwalk','Downey','Torrance','Whittier','La Habra','Buena Park','Bellflower','Hesperia','Victorville','Moreno Valley','Temecula','Murrieta','Modesto','Stockton','Elk Grove','Vacaville','Chico','Visalia','Bakersfield','Oxnard','Simi Valley','Santa Maria','Salinas','Turlock','Hanford'],
    'America/Phoenix':     ['Gilbert','Chandler','Peoria','Surprise','Goodyear','Buckeye','Casa Grande','Maricopa','Prescott Valley','Sierra Vista','Yuma','Oro Valley','Marana','Queen Creek'],
    'America/Denver':      ['Thornton','Arvada','Westminster','Longmont','Loveland','Greeley','Broomfield','Parker','Castle Rock','Grand Junction','Pueblo','Commerce City','Brighton','Ogden','Provo','Orem','Sandy','Layton'],
    'America/Chicago':     ['Cicero','Berwyn','Elgin','Waukegan','Joliet','Naperville','Schaumburg','Oak Lawn','Round Rock','Pflugerville','Katy','Pearland','League City','Mansfield','Grapevine','Olathe','Overland Park','Shawnee','Bentonville','Broken Arrow','Edmond','Metairie','Kenner','Hattiesburg','Franklin','Murfreesboro','Smyrna'],
    'America/New_York':    ['Yonkers','New Rochelle','Levittown','Hicksville','Bayonne','Hoboken','Passaic','Perth Amboy','Toms River','Cherry Hill','Bensalem','Levittown PA','Lancaster','Reading','Bethlehem','Silver Spring','Dundalk','Glen Burnie','Chesapeake','Suffolk','Greenville','Concord','Marietta','Sandy Springs','Kennesaw','Lawrenceville','Hialeah','Kendall','Coral Springs','Port St. Lucie','Palm Bay','Brandon','Clearwater','Lakeland'],
    'America/Anchorage':   ['Wasilla','Palmer','Eagle River','Kenai','Homer'],
    'Pacific/Honolulu':    ['Kaneohe','Kailua','Waipahu','Mililani','Kahului']
  };
  var FALLBACK = ['Rancho Cucamonga','Round Rock','Bensalem','Thornton','Gilbert','Kenner','Marietta'];

  function towns() {
    var tz = '';
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) {}
    if (TOWNS[tz]) return TOWNS[tz];
    // unknown zone: fall back to the region it belongs to rather than guessing
    var region = tz.split('/')[0];
    if (region === 'America') {
      var all = [];
      Object.keys(TOWNS).forEach(function (k) { if (k.indexOf('America/') === 0) all = all.concat(TOWNS[k]); });
      return all.length ? all : FALLBACK;
    }
    return FALLBACK;
  }

  var products = [];
  var tag = document.querySelector('[data-sp-products]');
  if (tag) { try { products = JSON.parse(tag.textContent) || []; } catch (e) {} }

  var cityEl = el.querySelector('[data-sp-city]'),
      prodEl = el.querySelector('[data-sp-prod]'),
      whenEl = el.querySelector('[data-sp-when]'),
      imgEl  = el.querySelector('[data-sp-img]');

  var shown = 0;
  var MAX   = parseInt(el.dataset.max, 10) || 5;
  var FIRST = (parseInt(el.dataset.firstDelay, 10) || 12) * 1000;
  var GAP   = (parseInt(el.dataset.gap, 10) || 26) * 1000;
  var DWELL = (parseInt(el.dataset.dwell, 10) || 6) * 1000;

  var queue = [];
  var pool = towns();
  var rand = function (a) { return a[Math.floor(Math.random() * a.length)]; };

  function ago(min) {
    if (min < 1) return 'just now';
    if (min === 1) return '1 minute ago';
    if (min < 60) return min + ' minutes ago';
    var h = Math.round(min / 60);
    return h === 1 ? 'about an hour ago' : h + ' hours ago';
  }

  function buildSimulated() {
    if (!products.length) return [];
    var out = [], usedCities = [], usedProducts = [], t = 2;

    function pickUnused(list, used) {
      // never repeat a city or product inside one visit — a name coming round
      // twice is the single clearest tell that these are generated
      var fresh = list.filter(function (x) { return used.indexOf(x) === -1; });
      var choice = rand(fresh.length ? fresh : list);
      used.push(choice);
      if (used.length > list.length - 1) used.length = 0;
      return choice;
    }

    for (var i = 0; i < MAX; i++) {
      var p = rand(products.filter(function (x) {
        return usedProducts.indexOf(x.title) === -1;
      }) .length ? products.filter(function (x) {
        return usedProducts.indexOf(x.title) === -1;
      }) : products);
      usedProducts.push(p.title);

      // times march backwards in irregular steps, the way a real order list
      // reads. Evenly spaced timestamps look generated at a glance.
      t += 3 + Math.floor(Math.random() * 22);
      out.push({
        city: pickUnused(pool, usedCities),
        product: p.title,
        url: p.url,
        img: p.img,
        minutesAgo: t
      });
    }
    // most recent first
    return out.sort(function (a, b) { return a.minutesAgo - b.minutesAgo; });
  }

  function render(o) {
    cityEl.textContent = 'Someone in ' + o.city;
    prodEl.textContent = o.product;
    whenEl.textContent = ago(o.minutesAgo);
    if (o.img) { imgEl.src = o.img; imgEl.alt = o.product; imgEl.hidden = false; }
    else imgEl.hidden = true;
    el.hidden = false;
    requestAnimationFrame(function () { el.classList.add('is-in'); });
    setTimeout(hide, DWELL);
  }

  function hide() {
    el.classList.remove('is-in');
    setTimeout(function () { el.hidden = true; }, 420);
  }

  function next() {
    if (shown >= MAX || !queue.length) {
      try { sessionStorage.setItem('bc_sp_at', String(Date.now())); } catch (e) {}
      return;
    }
    render(queue.shift());
    shown++;
    // jitter the cadence +/-35% so popups do not arrive on a metronome
    setTimeout(next, DWELL + GAP * (0.65 + Math.random() * 0.7));
  }

  el.querySelector('[data-sp-close]').addEventListener('click', function () {
    hide();
    shown = MAX;
    try { sessionStorage.setItem('bc_sp_at', String(Date.now())); } catch (e) {}
  });

  function start() {
    if (!queue.length) return;
    setTimeout(next, FIRST);
  }

  if (mode === 'feed' && el.dataset.feed) {
    fetch(el.dataset.feed, { credentials: 'omit' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        queue = (d.orders || []).slice(0, MAX);
        start();
      })
      // a dead feed shows nothing rather than silently inventing orders
      .catch(function () {});
  } else if (mode === 'simulated') {
    queue = buildSimulated();
    start();
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initSocialProof); else initSocialProof();

/* --------------------------------------------------------------------------
   Dispatch status. Four stages driven by the visitor's own clock, so the
   pressure it creates is real rather than theatrical: before opening the day's
   orders are queued, through the day packing is live, at the cutoff the van is
   loaded, and after collection the next slot is tomorrow. Weekends say so.
   -------------------------------------------------------------------------- */
function initDispatch() {
  var root = document.querySelector('[data-dispatch]');
  if (!root) return;

  /* SECURITY NOTE — read before editing the copy below.
     This band deliberately publishes NO real logistics timing. Naming the hour
     a loaded van leaves tells anyone reading the page exactly when and where
     product is on the street, which is a risk to the driver. So:
       - the only clock shown is the ORDER-BY cutoff, which is about what the
         customer has to do, not about when goods move;
       - later stages are described without times at all ("handed to the
         carrier", not "17:00 collection");
       - the configured hours drive the stage progression only. They are a
         presentation schedule and are not required to match the warehouse.
     The persuasion is unaffected: the pressure comes from the order deadline,
     which is the only part the customer can act on anyway. */

  var OPEN    = parseInt(root.dataset.open, 10) || 9;
  var CUTOFF  = parseInt(root.dataset.cutoff, 10) || 15;
  var LATE    = parseInt(root.dataset.collect, 10) || 17;

  var stages = Array.prototype.slice.call(root.querySelectorAll('[data-stage]'));
  var nowEl  = root.querySelector('[data-dispatch-now]');
  var kickEl = root.querySelector('[data-dispatch-kicker]');

  function pad(n) { return String(n).padStart(2, '0'); }

  /* the board keeps warehouse time — Pacific — no matter where the visitor is */
  function paNow() {
    try {
      var o = {};
      new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles', hour12: false,
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric'
      }).formatToParts(new Date()).forEach(function (p) { o[p.type] = p.value; });
      return new Date(o.year, o.month - 1, o.day, o.hour % 24, o.minute);
    } catch (e) { return new Date(); }
  }

  function tick() {
    var d = paNow();
    var h = d.getHours() + d.getMinutes() / 60;
    var weekend = d.getDay() === 0 || d.getDay() === 6;
    var live = -1, text = '', kicker = 'In the warehouse right now';

    if (weekend) {
      live = 0;
      kicker = 'Weekend mode';
      text = 'Monday&rsquo;s batch is filling up &mdash; <b>lock yours in early</b>';
    } else if (h < OPEN) {
      live = 0;
      kicker = 'First come, first shipped';
      text = 'Overnight orders go out <b>first</b> &mdash; get yours on the stack';
    } else if (h < CUTOFF) {
      live = 1;
      var mins = Math.floor((CUTOFF - h) * 60);
      var hh = Math.floor(mins / 60), mm = mins % 60;
      text = 'Packing right now &mdash; <b>'
           + (hh ? hh + 'h ' + pad(mm) + 'm' : mm + 'm') + '</b> left to ship today';
    } else if (h < LATE) {
      live = 2;
      kicker = 'Cutoff passed';
      text = 'Today&rsquo;s batch is boxed up &mdash; <b>next round ships tomorrow</b>';
    } else {
      live = 3;
      kicker = 'Lights out';
      text = 'Today&rsquo;s orders are gone &mdash; order now and you&rsquo;re <b>first out tomorrow</b>';
      root.classList.add('is-closed');
    }

    if (live !== 3) root.classList.remove('is-closed');
    nowEl.innerHTML = text;
    kickEl.textContent = kicker;

    stages.forEach(function (s, i) {
      s.classList.toggle('is-done', i < live);
      s.classList.toggle('is-live', i === live);
      var fill = s.querySelector('.dstage__fill');
      if (!fill) return;
      if (i < live) { fill.style.width = '100%'; return; }
      if (i > live) { fill.style.width = '0'; return; }
      var from = i === 0 ? 0 : (i === 1 ? OPEN : (i === 2 ? CUTOFF : LATE));
      var to   = i === 0 ? OPEN : (i === 1 ? CUTOFF : (i === 2 ? LATE : 24));
      var pct  = Math.max(6, Math.min(100, ((h - from) / (to - from)) * 100));
      fill.style.width = pct.toFixed(1) + '%';
    });
  }

  tick();
  setInterval(tick, 30000);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initDispatch); else initDispatch();

/* --------------------------------------------------------------------------
   The open sign.

   "Open now — shipping today" is a lie at 11pm, and a visitor who reads it at
   11pm and then sees their order ship two days later has learned not to trust
   the next promise the site makes. It now states whatever is actually true for
   the hour they are reading it in, and — this is the point — every state still
   gives a reason to order NOW. Closed is not the same as come back later:
   ordering overnight is what puts you at the front of the morning queue.

   No logistics detail is published here either. See initDispatch().
   -------------------------------------------------------------------------- */
function initOpenSign() {
  var signs = Array.prototype.slice.call(document.querySelectorAll('[data-open-sign]'));
  if (!signs.length) return;

  /* Warehouse hours, PACIFIC — the sign keeps the same clock the shipping
     promises keep. Open until 7pm PT; same-day shipping cuts at 5pm PT, and
     the sign stays honest about the difference. */
  var OPEN = 9, SHIP_CUT = 17, CLOSE = 19, NIGHT = 21;

  function paNow() {
    try {
      var o = {};
      new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles', hour12: false,
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric'
      }).formatToParts(new Date()).forEach(function (p) { o[p.type] = p.value; });
      return new Date(o.year, o.month - 1, o.day, o.hour % 24, o.minute);
    } catch (e) { return new Date(); }
  }

  function state() {
    var d = paNow(), h = d.getHours() + d.getMinutes() / 60, day = d.getDay();

    if (day === 6 || day === 0) {
      var next = day === 6 ? 'Monday' : 'tomorrow';
      return { cls: 'is-rest', text: 'Order now — first out ' + next };
    }
    if (h < 6)        return { cls: 'is-night', text: 'Order now — ships first thing' };
    if (h < OPEN)     return { cls: 'is-soon',  text: 'Order now — ships this morning' };
    if (h < SHIP_CUT) return { cls: 'is-open',  text: 'Open now — shipping today' };
    if (h < CLOSE)    return { cls: 'is-open',  text: 'Open late — first out tomorrow' };
    if (h < NIGHT)    return { cls: 'is-late',  text: 'Order now — first out tomorrow' };
    return { cls: 'is-night', text: 'Order now — ships first thing' };
  }

  function tick() {
    var s = state();
    signs.forEach(function (el) {
      el.classList.remove('is-open', 'is-soon', 'is-late', 'is-night', 'is-rest');
      el.classList.add(s.cls);
      var t = el.querySelector('.open-sign__txt');
      if (t && t.textContent !== s.text) t.textContent = s.text;
    });
  }

  tick();
  setInterval(tick, 60000);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initOpenSign); else initOpenSign();

/* --------------------------------------------------------------------------
   Bead popups: decide direction by measurement, and make the beads do
   something when clicked.

   The panel opens upwards by default because the rail usually has room above
   it. On the hero it often does not — the sticky header is right there — so
   before showing, we measure the gap between the bead and the bottom of the
   header and flip the panel downwards when it will not fit. Measured rather
   than assumed, so it stays correct at any viewport or header height.

   Clicking a bead carries the chosen pairing through to the product page,
   which is what a visitor pressing a flavour is actually trying to do.
   -------------------------------------------------------------------------- */
function initBeadPopups() {
  var beads = Array.prototype.slice.call(document.querySelectorAll('.flavrail .bead'));
  if (!beads.length) return;

  function headerBottom() {
    var h = document.querySelector('.header, .site-header, header');
    if (!h) return 0;
    var r = h.getBoundingClientRect();
    return (getComputedStyle(h).position === 'sticky' || getComputedStyle(h).position === 'fixed')
      ? r.bottom : 0;
  }

    var TOUCH = window.matchMedia('(hover: none)').matches;
  var GUTTER = 8;   // keep this much clear of the viewport edge

  function place(bead) {
    var pop = bead.querySelector('.bead__pop');
    if (!pop) return;

    // vertical: flip below the bead when there is no room above
    pop.classList.remove('bead__pop--below');
    var need = pop.offsetHeight + 22;
    var room = bead.getBoundingClientRect().top - headerBottom();
    if (room < need) pop.classList.add('bead__pop--below');

    /* Horizontal placement is handled in CSS: the first two beads anchor their
       card left and the last three anchor right, arrow moved to match. Nudging
       it from here fought those rules and misaligned the arrow. */
  }

  function closeAll(except) {
    beads.forEach(function (b) {
      if (b !== except) b.classList.remove('is-peeking');
    });
  }

  beads.forEach(function (b, i) {
    ['mouseenter', 'focus'].forEach(function (ev) {
      b.addEventListener(ev, function () { place(b); });
    });

    b.addEventListener('click', function (e) {
      /* No hover on a phone, so the pairing was unreadable — a tap went
         straight to the product page. First tap now opens the card, second
         follows the link. */
      if (TOUCH && !b.classList.contains('is-peeking')) {
        e.preventDefault();
        closeAll(b);
        b.classList.add('is-peeking');
        place(b);
        return;
      }
      var base = (window.BigChief && window.BigChief.duoUrl) || '/pages/cartridges';
      window.location.href = base + '?pair=' + i;
    });
  });

  if (TOUCH) {
    document.addEventListener('click', function (e) {
      if (!e.target.closest || !e.target.closest('.bead')) closeAll(null);
    });
  }

  window.addEventListener('resize', function () {
    beads.forEach(function (b) {
      var p = b.querySelector('.bead__pop');
      if (p) p.classList.remove('bead__pop--below');
      b.classList.remove('is-peeking');
    });
  }, { passive: true });
}

/* A pairing arriving as ?pair=N should already be selected on the product
   page — otherwise the click that got you there is thrown away. */
function initPairDeepLink() {
  var m = /[?&]pair=(\d+)/.exec(window.location.search);
  if (!m) return;
  var i = parseInt(m[1], 10);
  var target = document.querySelector('.jbead[data-pair="' + i + '"], [data-pair="' + i + '"]');
  if (target && target.click) target.click();
}

(function(){var go=function(){
  initBeadPopups();
  initPairDeepLink();
};if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',go);else go();})();

/* --------------------------------------------------------------------------
   Entry motion.

   One IntersectionObserver for the whole page rather than one per element, and
   each element is unobserved once it has arrived — a reveal that re-runs on
   every scroll past is worse than no reveal at all.

   Elements are tagged and staggered here rather than in the markup so that
   every section gets it automatically, including any added later in the theme
   editor. rootMargin fires slightly before the element reaches the viewport so
   the motion is finishing as it comes into view, not starting.
   -------------------------------------------------------------------------- */
function initRise() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  var TARGETS = [
    '.section__head', '.spot__copy', '.spot__stage',
    '.sh', '.btile', '.bcard', '.led__row', '.offer',
    '.cat', '.lineup__card', '.feat', '.svw__body', '.svw__stage',
    '.apparel__tile', '.faq__item', '.shipband__in'
  ].join(',');

  var els = Array.prototype.slice.call(document.querySelectorAll(TARGETS));
  if (!els.length) return;

  // stagger by position WITHIN a section, so a long grid does not end up with
  // a two-second delay on its last card
  var counts = {};
  els.forEach(function (el) {
    var sec = el.closest('section') || document.body;
    var key = sec.dataset.riseKey || (sec.dataset.riseKey = Math.random().toString(36).slice(2));
    counts[key] = (counts[key] || 0);
    el.style.setProperty('--i', Math.min(counts[key], 6));
    counts[key]++;
    el.setAttribute('data-rise', '');
  });

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      io.unobserve(e.target);          // arrive once, then stop watching
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  els.forEach(function (el) { io.observe(el); });

  /* FAIL-SAFE. An instant jump (anchor, scrollIntoView, fast flick) can
     teleport an element across the viewport between observer ticks, leaving
     whole sections stuck at opacity 0 — the "huge empty area" bug. Three
     nets under the observer:
       1. a sweep on every scroll (capture:true also hears inner containers)
          that reveals anything in or above the viewport,
       2. one early sweep for elements already on screen at load,
       3. a hard deadline: five seconds after load, everything is visible,
          animation or no animation. Content is never allowed to be missing. */
  function sweep() {
    els.forEach(function (el) {
      if (el.classList.contains('is-in')) return;
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) { el.classList.add('is-in'); io.unobserve(el); }
      else if (r.bottom <= 0) { el.classList.add('is-in'); io.unobserve(el); }
    });
  }
  var sweeping = false;
  document.addEventListener('scroll', function () {
    if (sweeping) return;
    sweeping = true;
    requestAnimationFrame(function () { sweeping = false; sweep(); });
  }, { passive: true, capture: true });
  setTimeout(sweep, 600);
  setTimeout(function () {
    els.forEach(function (el) { el.classList.add('is-in'); });
    io.disconnect();
  }, 5000);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initRise); else initRise();


/* ==========================================================================
   STRAIN BOARD + VERIFY SEQUENCE
   Ported out of the preview page so the Shopify templates behave the same.
   Both guard on their own markup being present, so they cost nothing on the
   pages that do not use them.
   ========================================================================== */
(function(){
  var PAIRS=[{"a": "Berry Colada", "b": "Pineapple Whip", "ca": "#C9268F", "cb": "#F2E27A"}, {"a": "Sugar Cookies", "b": "Pink Milk", "ca": "#E0552B", "cb": "#E8768C"}, {"a": "Jelly Roll", "b": "White Peach Tea", "ca": "#D8232A", "cb": "#E0776A"}, {"a": "Mamba", "b": "Tiger's Blood", "ca": "#3B3A98", "cb": "#D8232A"}, {"a": "Tropic Thunder", "b": "Passion Fruit Punch", "ca": "#7DB82E", "cb": "#D6248C"}, {"a": "Wabi Sabi", "b": "Green Matcha", "ca": "#5BB8D8", "cb": "#7DC142"}, {"a": "Sherbquake", "b": "Rocket Runtz", "ca": "#A87BC9", "cb": "#F0D93E"}, {"a": "Yuzu Ice", "b": "Cherry Cola", "ca": "#5BB8D8", "cb": "#C9202E"}, {"a": "Atomic Apple", "b": "Florida Orange", "ca": "#D8232A", "cb": "#E8631E"}, {"a": "Red Velvet Cake", "b": "Cupidz Candy", "ca": "#C41E2A", "cb": "#E8437F"}];
  var BASE=4400, DISC={1:0,3:20,5:25,10:30};
  var picks=[], want=3;

  var beads=[].slice.call(document.querySelectorAll('.jbead[data-pair]'));
  var cards=[].slice.call(document.querySelectorAll('.btile[data-pair]'));
  var panels=[].slice.call(document.querySelectorAll('[data-panel]'));
  var wash=document.querySelector('.pdp-hero__wash'), pool=document.querySelector('.pdp-hero__pool');
  var slotsEl=document.querySelector('[data-stack-slots]');
  var nudgeEl=document.querySelector('[data-stack-nudge]');
  /* Every script in this file shares one global scope, so an uncaught throw
     here silently kills everything defined after it — which is how the whole
     Shop All bundle builder ended up dead on its own page. These two are only
     present on the Duo board, so bail cleanly when they are not. */
  if (!slotsEl || !nudgeEl) return;

  function money(c){ return '$'+Math.floor(c/100)+'.'+String(c%100).padStart(2,'0'); }

  /* Preview always follows the LAST thing touched, so the stage keeps up with
     the eye even while the stack is being built. */
  function preview(i){
    panels.forEach(function(p){ p.classList.toggle('is-on', Number(p.dataset.panel)===i); });
    var p=PAIRS[i];
    [wash,pool].forEach(function(el){ if(el){ el.style.setProperty('--a',p.ca); el.style.setProperty('--b',p.cb); } });
  }

  function toggle(i){
    var at=picks.indexOf(i);
    if(at>-1){ picks.splice(at,1); }
    else { picks.push(i); }

    // The count drives the tier, not the other way round. Capping picks at the
    // selected tier meant adding a sixth flavour silently threw the first one
    // away — the customer is trying to spend MORE and the interface stops them.
    // `want` now grows to the smallest tier that fits what has been picked.
    var fits = TIERS.filter(function(t){ return t >= picks.length; })[0] || 10;
    if (fits > want) {
      want = fits;
      var radio = document.querySelector('input[name=stack][value="'+want+'"]');
      if (radio) radio.checked = true;
    }

    preview(i);
    render();
  }


  /* The board is where multi-buy actually happens, so the bar has to answer
     three questions without scrolling: how many have I got, what tier does that
     land me in, and what does it cost. It also names the NEXT tier in dollars
     per unit, because that is the number that moves someone from three to five. */
  var TIERS=[1,3,5,10], NAMES={1:'Just The One',3:'The Rotation',5:'The Five',10:'The Pack'};
  function tierFor(n){ var t=1; TIERS.forEach(function(x){ if(n>=x) t=x; }); return t; }

  function renderBar(){
    var bar=document.querySelector('[data-bbar]');
    if(!bar) return;
    var n=picks.length;
    bar.hidden = n===0;
    if(!n) return;

    var t=tierFor(n), pct=DISC[t]||0;
    var full=BASE*n, save=Math.floor(full*pct/100), now=full-save, each=Math.floor(now/n);
    var wasEl=bar.querySelector('[data-bbar-was]');
    wasEl.textContent = pct? money(full) : '';
    bar.querySelector('[data-bbar-now]').textContent = money(now);
    bar.querySelector('[data-bbar-each]').textContent = money(each)+' each';

    var next=TIERS.filter(function(x){ return x>n; })[0];
    var tier=bar.querySelector('[data-bbar-tier]');
    if(!next){
      tier.innerHTML='<b>That is the lot.</b> Every pairing we make, best price we do.';
    } else {
      var nextEach=Math.floor((BASE*next-Math.floor(BASE*next*DISC[next]/100))/next);
      // below the first discounted rung nothing has been "unlocked" yet, so say
      // what it takes to get there rather than congratulating a full-price pick
      tier.innerHTML = (pct > 0)
        ? '<b>'+NAMES[t]+'</b> unlocked &middot; add <b>'+(next-n)+'</b> more for <b>'+money(nextEach)+'</b> each'
        : 'Add <b>'+(next-n)+'</b> more to unlock <b>'+NAMES[next]+'</b> &mdash; '+money(nextEach)+' each';
    }

    var dots=bar.querySelector('[data-bbar-dots]');
    dots.innerHTML='';
    picks.forEach(function(i){
      var d=document.createElement('span'); d.className='bbar__dot';
      d.style.setProperty('--a',PAIRS[i].ca); d.style.setProperty('--b',PAIRS[i].cb);
      d.title=PAIRS[i].a+' & '+PAIRS[i].b;
      dots.appendChild(d);
    });
  }

  function render(){
    beads.forEach(function(b){
      var i=Number(b.dataset.pair), n=picks.indexOf(i);
      b.classList.toggle('is-picked', n>-1);
      b.setAttribute('aria-pressed', n>-1?'true':'false');
      var num=b.querySelector('.jbead__num');
      if(num) num.textContent = n>-1 ? (n+1) : '';
    });
    cards.forEach(function(c){
      var i=Number(c.dataset.pair), n=picks.indexOf(i);
      c.classList.toggle('is-picked', n>-1);
      c.setAttribute('aria-pressed', n>-1?'true':'false');
      var lbl=c.querySelector('.btile__add em');
      if(lbl) lbl.textContent = n>-1 ? 'Added' : 'Add';
      var num=c.querySelector('.btile__num');
      if(num) num.textContent = n>-1 ? (n+1) : '';
    });
    renderBar();

    document.querySelectorAll('[data-stack-n]').forEach(function(e){ e.textContent=picks.length; });
    document.querySelectorAll('[data-stack-max]').forEach(function(e){ e.textContent=want; });
    var clear=document.querySelector('[data-stack-clear]');
    if(clear) clear.hidden = picks.length===0;

    // slots make the empty space in the stack visible — an unfilled slot is a
    // far stronger prompt than a line of text asking you to add more
    slotsEl.innerHTML='';
    for(var s=0;s<want;s++){
      var d=document.createElement('span');
      if(picks[s]!=null){
        var p=PAIRS[picks[s]];
        d.className='slot slot--full';
        d.style.setProperty('--a',p.ca); d.style.setProperty('--b',p.cb);
        d.title=p.a+' & '+p.b;
      } else d.className='slot';
      slotsEl.appendChild(d);
    }

    var tiers=[1,3,5,10];
    var next=tiers.filter(function(t){ return t>picks.length; })[0];
    if(picks.length<want){
      var target = want===10?'The Pack':(want===5?'The Five':'The Rotation');
      nudgeEl.innerHTML='<b>'+(want-picks.length)+'</b> more to unlock <b>'+target+'</b>';
    } else if(next){
      var nowEach=Math.floor((BASE*want-Math.floor(BASE*want*DISC[want]/100))/want);
      var nextEach=Math.floor((BASE*next-Math.floor(BASE*next*DISC[next]/100))/next);
      nudgeEl.innerHTML='Take <b>'+next+'</b> instead and every one drops to <b>'+money(nextEach)
        +'</b> &mdash; '+money(nowEach-nextEach)+' off each';
    } else {
      nudgeEl.innerHTML='<b>Full board.</b> Every pairing, best price per gram we do.';
    }

    var total=BASE*want-Math.floor(BASE*want*DISC[want]/100);
    document.querySelector('[data-buy-total]').textContent=money(total);
    document.querySelector('[data-buy-label]').textContent =
      picks.length<want ? ((want-picks.length)+' to go') : ('Add '+want+' to cart');
    document.querySelector('[data-buy]').classList.toggle('is-waiting', picks.length<want);
  }

  beads.forEach(function(b){ b.addEventListener('click',function(){ toggle(Number(b.dataset.pair)); }); });
  cards.forEach(function(c){ c.addEventListener('click',function(){ toggle(Number(c.dataset.pair)); }); });

  document.querySelectorAll('input[name=stack]').forEach(function(r){
    r.addEventListener('change',function(){
      want=Number(r.value);
      if(picks.length>want) picks=picks.slice(-want);
      render();
    });
  });
  var clear=document.querySelector('[data-stack-clear]');
  if(clear) clear.addEventListener('click',function(){ picks=[]; render(); });

  preview(0); render();
})();

/* The verify sequence: four acts on a timer, driven by the same step list the
   copy already uses. It does not start until the section is on screen, and a
   click on any step jumps to that act and restarts the clock from there. */
(function(){
  var vfx = document.querySelector('[data-vfx]');
  if (!vfx) return;
  var acts  = [].slice.call(vfx.querySelectorAll('.vfx__act'));
  var steps = [].slice.call(document.querySelectorAll('[data-act-go]'));
  if (!acts.length) return;

  var HOLD = [2600, 2300, 2100, 4200];   // each act runs as long as its animation needs
  var i = 0, timer = null, running = false;

  function paint(n){
    i = (n + acts.length) % acts.length;
    acts.forEach(function(a, k){ a.classList.toggle('is-on', k === i); });
    steps.forEach(function(b, k){
      b.classList.toggle('is-on',  k === Math.min(i, steps.length - 1));
      b.classList.toggle('is-done', k <  Math.min(i, steps.length - 1));
    });
    if (i === 3) count();
  }

  /* the points tick up rather than appearing, so the number is the payoff */
  function count(){
    var el = vfx.querySelector('[data-pts]');
    if (!el) return;
    var to = 10000, t0 = null;
    (function step(ts){
      if (!t0) t0 = ts;
      var p = Math.min(1, (ts - t0) / 1400);
      var e = 1 - Math.pow(1 - p, 3);
      el.textContent = Math.round(to * e).toLocaleString();
      if (p < 1) requestAnimationFrame(step);
    })(performance.now());
  }

  function next(){ paint(i + 1); schedule(); }
  function schedule(){ clearTimeout(timer); timer = setTimeout(next, HOLD[i]); }
  function start(){ if (running) return; running = true; paint(0); schedule(); }
  function stop(){ running = false; clearTimeout(timer); }

  steps.forEach(function(b){
    b.addEventListener('click', function(){
      paint(parseInt(b.getAttribute('data-act-go'), 10) || 0);
      schedule();
    });
  });

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(function(es){
      es.forEach(function(e){ e.isIntersecting ? start() : stop(); });
    }, { threshold: .35 }).observe(vfx);
  } else { start(); }
})();

/* Header shares the page navy at rest; it takes its hairline back once it is
   overlapping content. */
(function(){
  var h=document.querySelector('.header'); if(!h) return;
  var on=false;
  function sync(){
    var want = window.scrollY > 8;
    if (want !== on) { on = want; h.setAttribute('data-stuck', on ? 'true' : 'false'); }
  }
  sync();
  addEventListener('scroll', sync, { passive: true });
})();

/* ==========================================================================
   SHOP ALL — the bundle builder
   Filters the grid, tracks what is picked, and shows what the NEXT pick is
   worth. The discount is applied by a Shopify function or discount code at
   checkout; this is the front end that makes the tier visible while choosing.
   ========================================================================== */
(function () {
  var root = document.querySelector('[data-shopall]');
  if (!root) return;

  var grid   = root.querySelector('[data-sa-grid]');
  var bar    = root.querySelector('[data-sabar]');
  var tiles  = [].slice.call(root.querySelectorAll('.satile'));
  var chips  = [].slice.call(root.querySelectorAll('.sachip'));
  var rungs  = [].slice.call(root.querySelectorAll('.sa__ladder li'));

  /* A tier set to 0% in the editor is OFF, not "use the default" — the old
     `|| 20` fallbacks resurrected tiers the merchant had switched off, so the
     bar promised discounts checkout would never apply. */
  var TIERS = [
    { q: parseInt(root.dataset.t1, 10), p: parseInt(root.dataset.p1, 10) },
    { q: parseInt(root.dataset.t2, 10), p: parseInt(root.dataset.p2, 10) },
    { q: parseInt(root.dataset.t3, 10), p: parseInt(root.dataset.p3, 10) }
  ].filter(function (t) { return t.q > 0 && t.p > 0; })
   .sort(function (a, b) { return a.q - b.q; });
  if (!TIERS.length) TIERS = [{ q: 2, p: 10 }];

  var picked = [];

  function money(c) {
    return '$' + (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* pips sit where the tiers actually fall, not at even thirds */
  var span = TIERS[TIERS.length - 1].q;
  root.querySelectorAll('[data-pip]').forEach(function (p) {
    var i = parseInt(p.getAttribute('data-pip'), 10) - 1;
    if (TIERS[i]) p.style.left = Math.min(100, (TIERS[i].q / span) * 100) + '%';
    else p.hidden = true;   // fewer tiers than pips — hide the spares
  });

  function tierFor(n) {
    var hit = null;
    TIERS.forEach(function (t) { if (n >= t.q) hit = t; });
    return hit;
  }

  var lastTier = 0, shownSave = 0;

  /* the saved figure counts up — a number that slides is noticed, one that
     swaps is not */
  function countTo(el, from, to) {
    var t0 = null;
    (function step(ts) {
      if (!t0) t0 = ts;
      var p = Math.min(1, (ts - t0) / 520);
      var e = 1 - Math.pow(1 - p, 3);
      el.textContent = money(Math.round(from + (to - from) * e));
      if (p < 1) requestAnimationFrame(step);
    })(performance.now());
  }

  function paint(animate) {
    var n = picked.length;
    var gross = picked.reduce(function (s, el) { return s + (parseInt(el.dataset.price, 10) || 0); }, 0);
    var tier = tierFor(n);
    var pct = tier ? tier.p : 0;
    var net = Math.round(gross * (100 - pct) / 100);
    var saved = gross - net;

    root.querySelector('[data-sa-n]').textContent = n;
    root.querySelector('[data-sa-now]').textContent = money(net);

    var wasEl = root.querySelector('[data-sa-was]');
    var saveEl = root.querySelector('[data-sa-save]');
    if (pct > 0) {
      wasEl.textContent = money(gross);
      saveEl.hidden = false;
      saveEl.innerHTML = 'You save <b>' + money(saved) + '</b> (' + pct + '%)';
    } else {
      wasEl.textContent = '';
      saveEl.hidden = true;
    }
    shownSave = saved;

    /* The nudge is arithmetic, not a rule. Naming what the next pick costs and
       what it hands back is the line that grows the order. */
    var next = null;
    TIERS.forEach(function (t) { if (!next && n < t.q) next = t; });
    var msg = root.querySelector('[data-sa-msg]');
    if (next) {
      var need = next.q - n;
      var avg = n ? Math.round(gross / n) : 0;
      var projGross = gross + avg * need;
      var projSave = Math.round(projGross * next.p / 100);
      var extra = projSave - saved;
      if (n > 0 && avg > 0 && extra > 0) {
        msg.innerHTML = 'Add ' + need + ' more &mdash; about ' + money(avg * need) +
                        ' gets you <b>' + money(extra) + '</b> back';
      } else {
        msg.innerHTML = 'Add ' + need + ' to unlock <b>' + next.p + '% off</b>';
      }
      msg.classList.toggle('is-hot', need === 1);
    } else {
      msg.innerHTML = 'Best price unlocked &mdash; <b>' + pct + '% off</b>';
      msg.classList.remove('is-hot');
    }

    root.querySelector('[data-sa-fill]').style.width = Math.min(100, (n / span) * 100) + '%';

    rungs.forEach(function (li, i) {
      var hit = TIERS[i] && n >= TIERS[i].q;
      var was = li.classList.contains('is-hit');
      li.classList.toggle('is-hit', !!hit);
      if (hit && !was && animate) {
        li.classList.remove('just-hit');
        void li.offsetWidth;
        li.classList.add('just-hit');
      }
    });

    /* a tier crossing gets its own moment, once, on the way up */
    if (animate && pct > lastTier) {
      bar.classList.remove('is-win');
      void bar.offsetWidth;
      bar.classList.add('is-win');
      setTimeout(function () { bar.classList.remove('is-win'); }, 900);
    }
    lastTier = pct;

    if (n > 0) { bar.hidden = false; requestAnimationFrame(function () { bar.classList.add('is-up'); }); }
    else { bar.classList.remove('is-up'); setTimeout(function () { if (!picked.length) bar.hidden = true; }, 340); }
  }

  tiles.forEach(function (t) {
    var btn = t.querySelector('[data-pick]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var on = t.classList.toggle('is-picked');
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) {
        picked.push(t);
        t.classList.remove('just-picked');
        void t.offsetWidth;
        t.classList.add('just-picked');
      } else {
        picked = picked.filter(function (x) { return x !== t; });
      }
      paint(true);
    });
  });

  paint(false);
})();

(function () {
  /* Category rail. The counts and from-prices are read off the tiles instead of
     being typed into the theme editor — a merchandiser adding a product should
     never have to remember to bump a number in two places. */
  var rail = document.querySelector('[data-sa-cats]');
  var grid = document.querySelector('[data-sa-grid]');
  if (!rail || !grid) return;

  var cats  = [].slice.call(rail.querySelectorAll('[data-filter]'));
  var tiles = [].slice.call(grid.querySelectorAll('.satile'));
  var countEl = document.querySelector('[data-sa-count]');

  function money(c) {
    return '$' + (c / 100).toFixed(c % 100 ? 2 : 0);
  }
  /* Forgiving matching: "prerolls" finds pre-roll / preroll / Pre Rolls,
     "hash-holes" finds hash-hole. Tags people actually typed should count. */
  function normWord(w) {
    return w.toLowerCase().replace(/[^a-z0-9]+/g, '').replace(/s$/, '');
  }
  function tokensOf(tile) {
    if (!tile._saTokens) {
      tile._saTokens = (tile.dataset.tags || '').split(/\s+/).map(normWord).filter(Boolean);
    }
    return tile._saTokens;
  }
  function matches(tile, key) {
    if (key === '*') return true;
    return tokensOf(tile).indexOf(normWord(key)) > -1;
  }

  /* fill each card with what is actually behind it */
  cats.forEach(function (c) {
    var key = c.dataset.filter;
    var hits = tiles.filter(function (t) { return matches(t, key); });
    var meta = c.querySelector('[data-cat-meta]');
    if (!meta) return;
    if (!hits.length) { c.classList.add('is-empty'); meta.textContent = 'Sold out'; return; }
    var low = Math.min.apply(null, hits.map(function (t) { return parseInt(t.dataset.price, 10) || 0; }));
    meta.textContent = hits.length + (key === '*' ? ' products' : ' · from ' + money(low));
  });

  function apply(key, announce) {
    grid.classList.add('is-swapping');
    var shown = 0, entering = [];

    tiles.forEach(function (t) {
      var keep = matches(t, key);
      var wasHidden = t.hidden;
      t.hidden = !keep;
      t.classList.remove('is-entering');
      if (keep) {
        shown++;
        if (wasHidden) entering.push(t);
      }
    });

    /* only the newly arrived tiles animate, and on a stagger — the ones that
       were already on screen stay put so the eye keeps its place */
    entering.forEach(function (t, i) {
      t.style.animationDelay = Math.min(i * 34, 300) + 'ms';
      void t.offsetWidth;
      t.classList.add('is-entering');
    });
    setTimeout(function () {
      grid.classList.remove('is-swapping');
      entering.forEach(function (t) { t.style.animationDelay = ''; });
    }, 480);

    cats.forEach(function (c) {
      var on = c.dataset.filter === key;
      c.classList.toggle('is-on', on);
      c.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    if (countEl) {
      var label = key === '*' ? 'everything'
        : (rail.querySelector('[data-filter="' + key + '"] .sacat__n') || {}).textContent || key;
      countEl.innerHTML = 'Showing <b>' + shown + '</b> ' +
        (shown === 1 ? 'product' : 'products') +
        (key === '*' ? '' : ' in ' + label.toLowerCase());
    }
    if (announce) {
      var active = rail.querySelector('.sacat.is-on');
      if (active && active.scrollIntoView) {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    }
  }

  cats.forEach(function (c) {
    c.addEventListener('click', function () { apply(c.dataset.filter, true); });
  });

  /* --------------------------------------------------------------------
     The bundle bar's primary button was markup only — data-sa-go existed but
     nothing ever listened to it, so the main call to action on this page did
     nothing when clicked. Every tile already carries its variant id, so the
     whole selection can go to /cart/add in one request.

     Note the discount itself is NOT applied here: the tier percentages in the
     bar are a display of a Shopify automatic discount that has to exist in the
     admin. Without that discount configured, the cart totals will not match
     the price this bar is quoting.
     -------------------------------------------------------------------- */
  var go = document.querySelector('[data-sa-go]');
  if (go) {
    go.addEventListener('click', function () {
      var chosen = [].slice.call(grid.querySelectorAll('.satile.is-picked'));
      if (!chosen.length) return;

      var items = chosen
        .map(function (t) { return { id: t.dataset.id, quantity: 1 }; })
        .filter(function (i) { return i.id; });
      if (!items.length) return;

      var label = go.innerHTML;
      go.disabled = true;
      go.textContent = 'Adding…';

      var routes = (window.BigChief && window.BigChief.routes) || {};
      fetch(routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Could not add'); });
          return r.json();
        })
        .then(function () {
          // same finish the product form uses: refresh state, then show the drawer
          var C = window.BigChiefCart;
          if (C && C.refresh) {
            return C.refresh().then(function () {
              if (C.drawer && C.drawer.open) C.drawer.open();
              else window.location.href = routes.cart || '/cart';
            });
          }
          window.location.href = routes.cart || '/cart';
        })
        .catch(function (err) {
          go.textContent = err.message || 'Could not add';
          setTimeout(function () { go.innerHTML = label; }, 2200);
        })
        .then(function () {
          go.disabled = false;
          if (go.textContent === 'Adding…') go.innerHTML = label;
        });
    });
  }

  /* deep links: /pages/shop-all#carts opens on that category */
  var hash = (location.hash || '').replace('#', '');
  apply(hash && cats.some(function (c) { return c.dataset.filter === hash; }) ? hash : '*', false);
})();

/* --------------------------------------------------------------------------
   THE LINEUP RAIL
   The rail itself is CSS scroll-snap and works with this script absent. What
   this adds is the part that makes it feel authored: the section takes on the
   active product's accent as you swipe, and the named tabs stay in step.

   An IntersectionObserver on the scroller decides which card is live, rather
   than a scroll handler doing arithmetic on every frame.
   -------------------------------------------------------------------------- */
function initLineupRail() {
  var rails = [].slice.call(document.querySelectorAll('[data-rail]'));
  if (!rails.length) return;

  rails.forEach(function (rail) {
    var scroller = rail.querySelector('[data-rail-scroller]');
    var cards    = [].slice.call(rail.querySelectorAll('[data-rail-card]'));
    var tabs     = [].slice.call(rail.querySelectorAll('[data-rail-tab]'));
    if (!scroller || !cards.length) return;

    function setLive(i) {
      cards.forEach(function (c, x) { c.classList.toggle('is-live', x === i); });
      tabs.forEach(function (t, x) {
        t.classList.toggle('is-on', x === i);
        t.setAttribute('aria-selected', x === i ? 'true' : 'false');
      });
      var accent = cards[i] && cards[i].style.getPropertyValue('--accent');
      if (accent) rail.style.setProperty('--rail-accent', accent.trim());
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        // the most-visible card wins, so a half-swipe does not flicker
        var best = null;
        entries.forEach(function (e) {
          if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
        });
        if (best && best.isIntersecting && best.intersectionRatio > 0.5) {
          setLive(cards.indexOf(best.target));
        }
      }, { root: scroller, threshold: [0.25, 0.5, 0.75, 1] });
      cards.forEach(function (c) { io.observe(c); });
    }

    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () {
        cards[i].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        setLive(i);
      });
    });

    /* On wide screens the panels ARE the navigation — the collapsed ones open
       on hover, and on click for anyone navigating without a pointer. The rail
       is a scroller below that breakpoint, where the observer already handles
       which card is live, so this only binds above it. */
    var wide = window.matchMedia('(min-width: 62em)');
    function bindPanels() {
      cards.forEach(function (c, i) {
        if (c.dataset.panelBound) return;
        c.dataset.panelBound = '1';
        c.addEventListener('mouseenter', function () {
          if (wide.matches) setLive(i);
        });
        c.addEventListener('click', function () {
          if (wide.matches) setLive(i);
        });
        c.addEventListener('focusin', function () {
          if (wide.matches) setLive(i);
        });
      });
    }
    bindPanels();

    setLive(0);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLineupRail);
} else {
  initLineupRail();
}
document.addEventListener('shopify:section:load', initLineupRail);

/* Finish swatches on the spotlight panels — presentational selection: the
   tapped swatch takes the ring and the name label follows it. */
function initWays() {
  document.querySelectorAll('[data-ways]').forEach(function (row) {
    if (row.dataset.bound) return;
    row.dataset.bound = '1';
    var name = row.querySelector('[data-way-name]');
    row.addEventListener('click', function (e) {
      var btn = e.target.closest('.way');
      if (!btn) return;
      row.querySelectorAll('.way').forEach(function (w) { w.classList.toggle('is-on', w === btn); });
      if (name) name.textContent = (btn.dataset.way || '').charAt(0).toUpperCase() + (btn.dataset.way || '').slice(1);
    });
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initWays);
else initWays();
document.addEventListener('shopify:section:load', initWays);

/* --------------------------------------------------------------------------
   Spotlight bundle stackers. Tap +, a product pops into the row and the price
   recomputes; crossing the tier springs the discount badge. Below the tier the
   maths shows full price, which is itself the pitch for adding one more.
   -------------------------------------------------------------------------- */
/* A chip that carries a flavour orb instead of authored colours inherits the
   orb's two tones, so dots, rings and cards all read the right colour on
   pages where the palette comes from the product rather than the editor. */
function chipPaint(c) {
  if (c.style.getPropertyValue('--a')) return;
  var orb = c.querySelector('.orb');
  if (!orb) return;
  var a = orb.style.getPropertyValue('--c1');
  var b = orb.style.getPropertyValue('--c2');
  if (a) c.style.setProperty('--a', a.trim());
  c.style.setProperty('--b', (b || a).trim());
}

/* A loud little speech bubble over the strain chips: "Pick a flavour!" —
   shared by the stacker, its add button and the kit, so nobody is left
   wondering why the button did nothing. */
function askForFlavour(box) {
  if (!box) return;
  box.classList.remove('is-nudge'); void box.offsetWidth;
  box.classList.add('is-nudge');
  var call = box.querySelector('.straincall');
  if (!call) {
    call = document.createElement('span');
    call.className = 'straincall';
    call.setAttribute('role', 'status');
    call.innerHTML = '<b>Pick a flavor!</b> tap a strain below 👇';
    box.appendChild(call);
  }
  call.classList.remove('is-show'); void call.offsetWidth;
  call.classList.add('is-show');
  clearTimeout(call._t);
  call._t = setTimeout(function () { call.classList.remove('is-show'); }, 2400);
  box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  if (navigator.vibrate) { try { navigator.vibrate([12, 30, 12]); } catch (e) {} }
}

function initSpotStackers() {
  document.querySelectorAll('[data-stacker]').forEach(function (el) {
    if (el.dataset.bound) return;
    el.dataset.bound = '1';

    /* Flavour-aware mode: when the strain chips carry real variants, every
       unit in the stack is a chosen flavour. The + button refuses to guess —
       first press nudges the chips until a flavour is tapped — and each thumb
       wears its flavour's two-colour dot so the stack reads back what you
       picked. */
    var spot = el.closest('.spot') || el.closest('[data-bundle-scope]');
    var chips = spot ? [].slice.call(spot.querySelectorAll('.strains__list [data-strain]')) : [];
    var flavoured = chips.length > 0;   /* selection UX always on when strains exist */
    var current = -1;
    var picks = [];
    var strainsBox = spot ? spot.querySelector('[data-strains]') : null;

    chips.forEach(function (c, i) {
      c.dataset.cb = '1';   /* claimed by the stacker — the loose binder skips it */
      chipPaint(c);
      if (c.classList.contains('is-on')) current = i;   /* a preselected variant counts */
      function choose() {
        current = i;
        chips.forEach(function (x) { x.classList.toggle('is-on', x === c); x.setAttribute('aria-pressed', x === c ? 'true' : 'false'); });
        if (strainsBox) strainsBox.classList.remove('is-nudge');
        /* the stack starts pre-loaded at the tier quantity — those units have
           no flavour yet, so the first pick claims them all and every thumb
           gets its dot */
        for (var k = 0; k < qty; k++) { if (picks[k] == null) picks[k] = i; }
        dressThumbs();
        /* flash the tasting card so touch users see it too */
        c.classList.remove('is-flash'); void c.offsetWidth; c.classList.add('is-flash');
        clearTimeout(c._flashT);
        c._flashT = setTimeout(function () { c.classList.remove('is-flash'); }, 1200);
        if (navigator.vibrate) { try { navigator.vibrate(6); } catch (e) {} }
      }
      c.addEventListener('click', choose);
      c.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(); } });
    });
    var base = parseInt(el.dataset.price, 10) || 0;
    var tier = parseInt(el.dataset.tier, 10) || 2;
    var disc = parseInt(el.dataset.disc, 10) || 0;
    var max  = parseInt(el.dataset.max, 10) || 6;
    var things = el.querySelector('[data-stacker-things]');
    var badge  = el.querySelector('[data-stacker-badge]');
    var note   = el.querySelector('[data-stacker-note]');
    var wasEl  = el.querySelector('[data-stacker-was]');
    var nowEl  = el.querySelector('[data-stacker-now]');
    var proto  = things && things.firstElementChild ? things.firstElementChild.cloneNode(true) : null;
    if (!things || !proto || !base) return;
    var qty = things.children.length || tier;

    function money(c){ return '$' + Math.floor(c/100) + '.' + String(c%100).padStart(2,'0'); }

    function render(added) {
      while (things.children.length > qty) things.removeChild(things.lastElementChild);
      while (things.children.length < qty) {
        var t = proto.cloneNode(true);
        things.appendChild(t);
        if (added) { t.classList.add('is-new'); }
      }
      var pct  = qty >= tier ? disc : 0;
      var full = base * qty;
      var save = Math.floor(full * pct / 100);
      var now  = full - save;
      var each = Math.floor(now / qty);
      if (wasEl) { wasEl.textContent = pct ? money(full) : ''; }
      if (nowEl) {
        nowEl.textContent = money(now);
        nowEl.classList.remove('is-pop'); void nowEl.offsetWidth; nowEl.classList.add('is-pop');
      }
      if (note) {
        note.innerHTML = pct
          ? money(each) + ' each &middot; save ' + money(save)
          : (qty === 1 ? money(base) + ' &middot; add ' + (tier - 1) + ' more for −' + disc + '%'
                       : money(each) + ' each · ' + (tier - qty) + ' more unlocks −' + disc + '%');
      }
      if (badge) {
        var on = pct > 0;
        var wasOn = badge.classList.contains('is-on');
        badge.classList.toggle('is-on', on);
        if (on && !wasOn) { badge.classList.remove('is-spring'); void badge.offsetWidth; badge.classList.add('is-spring'); }
      }
      if (navigator.vibrate) { try { navigator.vibrate(8); } catch (e) {} }
      /* the go button always names the quantity it will add */
      var goBtn = el.querySelector('[data-stacker-add]');
      var goLbl = el.querySelector('[data-stacker-add-label]');
      if (goBtn && goLbl && !goBtn.disabled) goLbl.textContent = 'Add ' + qty + ' to cart';
    }

    function dressThumbs() {
      if (!flavoured || !things) return;
      [].slice.call(things.children).forEach(function (t, i) {
        var pick = picks[i];
        if (pick != null && chips[pick]) {
          var cs = getComputedStyle(chips[pick]);
          t.style.setProperty('--a', cs.getPropertyValue('--a'));
          t.style.setProperty('--b', cs.getPropertyValue('--b'));
          t.classList.add('has-flav');
          var nm = chips[pick].querySelector('.strains__name');
          t.title = (nm ? nm.textContent : chips[pick].textContent).trim();
        } else {
          t.classList.remove('has-flav');
        }
      });
    }

    el.querySelector('[data-stacker-plus]').addEventListener('click', function(){
      if (qty >= max) return;
      if (flavoured && current < 0) {
        /* no flavour chosen — say so instead of guessing */
        askForFlavour(strainsBox);
        return;
      }
      qty++;
      if (flavoured) picks.push(current);
      render(true); dressThumbs();
    });
    el.querySelector('[data-stacker-minus]').addEventListener('click', function(){
      if (qty <= 1) return;
      qty--;
      if (flavoured) picks.pop();
      render(false); dressThumbs();
    });

    /* Every unit in the stack is its own object. Hover (or tap, on touch)
       reveals an × on that unit; the × removes exactly that one, and the
       remaining flavour dots shift with their units because the pick list is
       spliced at the same index. */
    function disarmThumbs() {
      [].forEach.call(things.querySelectorAll('.is-armed'), function (n) { n.classList.remove('is-armed'); });
    }
    things.addEventListener('click', function (e) {
      var t = e.target.closest('.stackthing');
      if (!t || !things.contains(t)) return;
      var idx = [].indexOf.call(things.children, t);
      if (e.target.closest('.stackthing__x')) {
        if (qty <= 1) return;
        t.classList.add('is-going');
        if (navigator.vibrate) { try { navigator.vibrate(10); } catch (err) {} }
        setTimeout(function () {
          qty--;
          if (flavoured && idx < picks.length) picks.splice(idx, 1);
          /* rebuild from data so the unit that leaves is the one that was × */
          while (things.firstElementChild) things.removeChild(things.firstElementChild);
          render(false); dressThumbs(); disarmThumbs();
        }, 150);
        return;
      }
      /* tapping the unit arms its × — the only path on touch screens */
      var was = t.classList.contains('is-armed');
      disarmThumbs();
      if (!was) t.classList.add('is-armed');
    });

    /* the single-item escape hatch: one unit of the chosen flavour, no
       bundle required — bundles persuade, they never gatekeep */
    var oneBtn = el.querySelector('[data-stacker-one]');
    if (oneBtn && flavoured) {
      var oneHome = oneBtn.innerHTML;
      oneBtn.addEventListener('click', function () {
        var c = current > -1 ? chips[current] : null;
        if (!c) { askForFlavour(strainsBox); return; }
        if (!c.dataset.var) {
          oneBtn.textContent = 'Flavour not linked';
          setTimeout(function () { oneBtn.innerHTML = oneHome; }, 2200);
          return;
        }
        oneBtn.disabled = true;
        oneBtn.textContent = 'Adding one…';
        var routes = (window.BigChief && window.BigChief.routes) || {};
        fetch(routes.cartAdd || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(c.dataset.var, 10), quantity: 1 }] })
        })
        .then(function (r) { if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Could not add'); }); return r.json(); })
        .then(function () {
          oneBtn.textContent = '\u2713 In the cart';
          if (navigator.vibrate) { try { navigator.vibrate([10, 40, 14]); } catch (e) {} }
          var C = window.BigChiefCart;
          if (C && C.refresh) return C.refresh().then(function () { if (C.drawer && C.drawer.open) C.drawer.open(); });
        })
        .catch(function (err) { oneBtn.textContent = err.message || 'Could not add'; })
        .then(function () {
          setTimeout(function () { oneBtn.disabled = false; oneBtn.innerHTML = oneHome; }, 2400);
        });
      });
    }

    /* real add: aggregate the picked variants and post them in one call */
    var addBtn = el.querySelector('[data-stacker-add]');
    if (addBtn && flavoured) {
      var addLabel = el.querySelector('[data-stacker-add-label]');
      addBtn.addEventListener('click', function () {
        if (picks.length < qty || picks.length === 0) {
          askForFlavour(strainsBox);
          return;
        }
        var counts = {};
        picks.forEach(function (i) {
          var v = chips[i] && chips[i].dataset.var;
          if (v) counts[v] = (counts[v] || 0) + 1;
        });
        var items = Object.keys(counts).map(function (v) { return { id: parseInt(v, 10), quantity: counts[v] }; });
        if (!items.length) {
          /* chips picked but none carries a variant id — the product's variant
             titles don't contain the chip names, so say it instead of dying */
          if (addLabel) {
            addLabel.textContent = 'Flavour not linked — check variant names';
            setTimeout(function () { addLabel.textContent = 'Add ' + qty + ' to cart'; }, 2600);
          }
          return;
        }
        addBtn.disabled = true;
        if (addLabel) addLabel.textContent = 'Adding…';
        var routes = (window.BigChief && window.BigChief.routes) || {};
        fetch(routes.cartAdd || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
        .then(function (r) { if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Could not add'); }); return r.json(); })
        .then(function () {
          if (addLabel) addLabel.textContent = '✓ In the cart';
          if (navigator.vibrate) { try { navigator.vibrate([10, 40, 14]); } catch (e) {} }
          var C = window.BigChiefCart;
          if (C && C.refresh) return C.refresh().then(function () { if (C.drawer && C.drawer.open) C.drawer.open(); });
        })
        .catch(function (err) { if (addLabel) addLabel.textContent = err.message || 'Could not add'; })
        .then(function () {
          setTimeout(function () {
            addBtn.disabled = false;
            if (addLabel) addLabel.textContent = 'Add ' + qty + ' to cart';
          }, 2600);
        });
      });
    }
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initSpotStackers);
else initSpotStackers();
document.addEventListener('shopify:section:load', initSpotStackers);

/* --------------------------------------------------------------------------
   Kit add-to-cart. The finish swatches choose the companion's real variant and
   swap its image; the button posts both items in one call and opens the cart
   drawer — the same finish the Shop All builder uses.
   -------------------------------------------------------------------------- */
function initKitCart() {
  document.querySelectorAll('[data-kit]').forEach(function (kit) {
    if (kit.dataset.bound) return;
    kit.dataset.bound = '1';
    var img = kit.querySelector('[data-kit-img]');
    var btn = kit.querySelector('[data-kit-add]');
    var spot = kit.closest('.spot') || kit.closest('[data-bundle-scope]');
    var ways = spot ? spot.querySelector('[data-ways]') : null;

    if (ways && img) {
      ways.addEventListener('click', function (e) {
        var w = e.target.closest('.way');
        if (!w) return;
        var way = w.dataset.way || '';
        var src = img.getAttribute('data-img-' + way);
        var pic = img.querySelector('img');
        if (src && pic) {
          pic.style.opacity = '0';
          setTimeout(function () { pic.src = src; pic.onload = function(){ pic.style.opacity = '1'; }; }, 120);
        }
        var v = img.getAttribute('data-var-' + way);
        if (v) kit.dataset.kitVar = v;
        kit.classList.remove('is-flip'); void kit.offsetWidth; kit.classList.add('is-flip');
      });
    }

    if (!btn) return;
    var label = kit.querySelector('[data-kit-add-label]');
    btn.addEventListener('click', function () {
      /* The main product has flavours of its own — the kit honours the strain
         chip the customer picked. If chips exist but none is chosen, ask for
         the flavour instead of guessing. */
      var mainId = parseInt(kit.dataset.kitMain, 10);
      var chipsBox = spot ? spot.querySelector('[data-strains]') : null;
      var chosen = spot ? spot.querySelector('.strains__list [data-strain].is-on') : null;
      var anyChips = spot ? spot.querySelector('.strains__list [data-strain]') : null;
      if (anyChips && !chosen) {
        askForFlavour(chipsBox);
        if (label) { label.textContent = 'Pick a flavor first'; setTimeout(function(){ label.textContent='Add the kit'; }, 1800); }
        return;
      }
      if (chosen) {
        if (chosen.dataset.var) {
          mainId = parseInt(chosen.dataset.var, 10);
        } else {
          /* a flavour is picked but carries no variant id — adding the default
             would put the WRONG flavour in the cart, so say it instead */
          if (label) { label.textContent = 'Flavour not linked — check variant names'; setTimeout(function(){ label.textContent='Add the kit'; }, 2600); }
          return;
        }
      }
      var items = [
        { id: mainId, quantity: 1 },
        { id: parseInt(kit.dataset.kitVar, 10), quantity: 1 }
      ].filter(function (i) { return i.id; });
      if (items.length < 2) {
        /* never a dead click: this means the pickers aren't assigned */
        if (label) { label.textContent = 'Kit not linked — assign products'; setTimeout(function(){ label.textContent='Add the kit'; }, 2600); }
        return;
      }
      btn.disabled = true;
      if (label) label.textContent = 'Adding…';
      var routes = (window.BigChief && window.BigChief.routes) || {};
      fetch(routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Could not add'); });
        return r.json();
      })
      .then(function () {
        if (label) label.textContent = '✓ In the cart';
        btn.classList.add('is-done');
        if (navigator.vibrate) { try { navigator.vibrate([10, 40, 14]); } catch (e) {} }
        var C = window.BigChiefCart;
        if (C && C.refresh) return C.refresh().then(function () { if (C.drawer && C.drawer.open) C.drawer.open(); });
      })
      .catch(function (err) {
        if (label) label.textContent = err.message || 'Could not add';
      })
      .then(function () {
        setTimeout(function () {
          btn.disabled = false;
          btn.classList.remove('is-done');
          if (label) label.textContent = 'Add the kit';
        }, 2600);
      });
    });
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initKitCart);
else initKitCart();
document.addEventListener('shopify:section:load', initKitCart);

/* --------------------------------------------------------------------------
   The story deal box ("Any 2 DUOs — mix the pairings") as a real add. Tap
   pairings above to fill the deal's bead slots; when the pair is full the
   button adds them all in one call. A tap past full starts a fresh pair.
   Registered BEFORE the loose binder so these chips are claimed first.
   -------------------------------------------------------------------------- */
function initDuoDeal() {
  document.querySelectorAll('[data-duodeal]').forEach(function (box) {
    if (box.dataset.bound) return;
    box.dataset.bound = '1';
    var scope = box.closest('.spot') || box.closest('section') || document;
    var chips = [].slice.call(scope.querySelectorAll('.strains__list [data-strain]'));
    var slots = box.querySelector('[data-duodeal-slots]');
    var btn   = box.querySelector('[data-duodeal-add]');
    var label = box.querySelector('[data-duodeal-label]');
    if (!chips.length || !slots || !btn) return;
    var want = parseInt(box.dataset.qty, 10) || 2;
    var unit = parseInt(box.dataset.unit, 10) || 0;
    var dealNow = parseInt(box.dataset.now, 10) || 0;
    var picks = [];
    var strainsBox = scope.querySelector('[data-strains]');
    function money(c) { return '$' + Math.floor(c / 100) + '.' + String(c % 100).padStart(2, '0'); }

    function paint() {
      [].slice.call(slots.children).forEach(function (s, i) {
        var p = picks[i];
        if (p != null && chips[p]) {
          var cs = getComputedStyle(chips[p]);
          s.style.setProperty('--a', cs.getPropertyValue('--a'));
          s.style.setProperty('--b', cs.getPropertyValue('--b'));
          s.classList.add('is-full');
          var nm = chips[p].querySelector('.strains__name');
          s.title = (nm ? nm.textContent : '').trim();
        } else {
          s.classList.remove('is-full');
          s.removeAttribute('title');
        }
      });
      chips.forEach(function (c, i) {
        var on = picks.indexOf(i) !== -1;
        c.classList.toggle('is-on', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (label && !btn.disabled) {
        var n = picks.length;
        if (n === 0) {
          label.textContent = 'Pick a pairing to start';
        } else if (n < want) {
          /* singles are welcome — the deal is an upgrade, not a gate */
          var solo = unit ? ' — ' + money(unit * n) : '';
          label.textContent = 'Add ' + n + ' to cart' + solo;
        } else {
          var total = dealNow || unit * want;
          label.textContent = 'Add ' + want + ' to cart' + (total ? ' — ' + money(total) : '');
        }
      }
      box.classList.toggle('is-ready', picks.length >= 1);
    }

    chips.forEach(function (c, i) {
      c.dataset.cb = '1';
      chipPaint(c);
      function tap() {
        if (picks.length >= want) picks = [];
        picks.push(i);
        c.classList.remove('is-flash'); void c.offsetWidth; c.classList.add('is-flash');
        clearTimeout(c._flashT);
        c._flashT = setTimeout(function () { c.classList.remove('is-flash'); }, 1200);
        if (navigator.vibrate) { try { navigator.vibrate(6); } catch (e) {} }
        paint();
      }
      c.addEventListener('click', tap);
      c.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tap(); } });
    });

    btn.addEventListener('click', function () {
      if (picks.length === 0) { askForFlavour(strainsBox); return; }
      var counts = {}, missing = false;
      picks.forEach(function (i) {
        var v = chips[i] && chips[i].dataset.var;
        if (v) counts[v] = (counts[v] || 0) + 1; else missing = true;
      });
      var items = Object.keys(counts).map(function (v) { return { id: parseInt(v, 10), quantity: counts[v] }; });
      if (missing || !items.length) {
        if (label) { label.textContent = 'Pairing not linked — check variant names'; setTimeout(paint, 2600); }
        return;
      }
      btn.disabled = true;
      if (label) label.textContent = 'Adding…';
      var routes = (window.BigChief && window.BigChief.routes) || {};
      fetch(routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
      .then(function (r) { if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Could not add'); }); return r.json(); })
      .then(function () {
        if (label) label.textContent = '✓ In the cart';
        if (navigator.vibrate) { try { navigator.vibrate([10, 40, 14]); } catch (e) {} }
        var C = window.BigChiefCart;
        if (C && C.refresh) return C.refresh().then(function () { if (C.drawer && C.drawer.open) C.drawer.open(); });
      })
      .catch(function (err) { if (label) label.textContent = err.message || 'Could not add'; })
      .then(function () {
        setTimeout(function () { btn.disabled = false; picks = []; paint(); }, 2600);
      });
    });

    paint();
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initDuoDeal);
else initDuoDeal();
document.addEventListener('shopify:section:load', initDuoDeal);

/* --------------------------------------------------------------------------
   Chips that live outside a bundle stacker — the DUO story rail, the product
   pages — still select and still pop their tasting card. They just have no
   cart to feed, so that is all they do.
   -------------------------------------------------------------------------- */
function initLooseChips() {
  document.querySelectorAll('.strains__list [data-strain]').forEach(function (c) {
    if (c.dataset.cb) return;
    if (c.closest('[data-jar]')) return;   /* the jar binds its own, later in the file */
    c.dataset.cb = '1';
    chipPaint(c);
    function flash() {
      var list = c.closest('.strains__list');
      if (list) {
        [].forEach.call(list.querySelectorAll('[data-strain]'), function (x) {
          x.classList.toggle('is-on', x === c);
          x.setAttribute('aria-pressed', x === c ? 'true' : 'false');
        });
      }
      c.classList.remove('is-flash'); void c.offsetWidth; c.classList.add('is-flash');
      clearTimeout(c._flashT);
      c._flashT = setTimeout(function () { c.classList.remove('is-flash'); }, 1200);
      if (navigator.vibrate) { try { navigator.vibrate(6); } catch (e) {} }
    }
    c.addEventListener('click', flash);
    c.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); flash(); } });
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initLooseChips);
else initLooseChips();
document.addEventListener('shopify:section:load', initLooseChips);

/* --------------------------------------------------------------------------
   PREROLL JAR. Tap a strain — a cone drops into the glass, tip up, angled
   like a bouquet. Tap a cone to pull it back out. Tiers unlock as it fills
   and the button narrates. Same variant contract as every other builder.
   -------------------------------------------------------------------------- */
function initPrerollJar() {
  document.querySelectorAll('[data-jar]').forEach(function (root) {
    if (root.dataset.bound) return;
    root.dataset.bound = '1';
    var chips = [].slice.call(root.querySelectorAll('.strains__list [data-strain]'));
    var cones = root.querySelector('[data-jar-cones]');
    var note  = root.querySelector('[data-jar-note]');
    var wasEl = root.querySelector('[data-jar-was]');
    var nowEl = root.querySelector('[data-jar-now]');
    var cnt   = root.querySelector('[data-jar-count]');
    var tiersEl = root.querySelector('[data-jar-tiers]');
    var btn   = root.querySelector('[data-jar-add]');
    var label = root.querySelector('[data-jar-add-label]');
    var strainsBox = root.querySelector('[data-strains]');
    if (!chips.length || !cones) return;

    var base = parseInt(root.dataset.price, 10) || 900;
    var max  = parseInt(root.dataset.max, 10) || 12;
    var tiers = (root.dataset.tiers || '').split(',').map(function (t) {
      var b = t.split(':');
      return { q: parseInt(b[0], 10) || 0, pct: parseInt(b[1], 10) || 0, name: (b[2] || '').trim() };
    }).filter(function (t) { return t.q > 0; }).sort(function (a, b) { return a.q - b.q; });
    var picks = [];

    if (tiersEl) {
      tiersEl.innerHTML = tiers.map(function (t) {
        return '<li data-tq="' + t.q + '"><b>' + t.q + '+</b><i>' + t.name + '</i><em>&minus;' + t.pct + '%</em></li>';
      }).join('');
    }

    function money(c) { return '$' + Math.floor(c / 100) + '.' + String(c % 100).padStart(2, '0'); }
    function pctFor(q) {
      var p = 0;
      tiers.forEach(function (t) { if (q >= t.q) p = t.pct; });
      return p;
    }

    function paint() {
      var q = picks.length;
      var pct = pctFor(q);
      var full = base * q;
      var now = full - Math.floor(full * pct / 100);
      if (cnt) { cnt.hidden = q === 0; cnt.textContent = q; }
      if (wasEl) { wasEl.hidden = !(q && pct); wasEl.textContent = money(full); }
      if (nowEl) { nowEl.hidden = q === 0; nowEl.textContent = money(now); }
      if (note) {
        if (q === 0) note.textContent = 'Tap a strain to start rolling';
        else {
          var next = null;
          tiers.forEach(function (t) { if (q < t.q && !next) next = t; });
          note.textContent = pct
            ? money(Math.floor(now / q)) + ' a roll · saving ' + pct + '%'
            : (next ? (next.q - q) + ' more unlocks ' + next.name + ' (−' + next.pct + '%)' : q + ' rolled up');
        }
      }
      if (tiersEl) {
        [].forEach.call(tiersEl.children, function (li) {
          li.classList.toggle('is-hit', picks.length >= parseInt(li.dataset.tq, 10));
        });
      }
      if (label && btn && !btn.disabled) {
        label.textContent = q === 0 ? 'Pack the jar' : 'Add ' + q + ' to cart — ' + money(now);
      }
      root.classList.toggle('is-live', q > 0);
    }

    function drop(i) {
      var c = document.createElement('i');
      c.className = 'cone';
      var cs = getComputedStyle(chips[i]);
      c.style.setProperty('--a', cs.getPropertyValue('--a'));
      c.style.setProperty('--b', cs.getPropertyValue('--b'));
      c.style.setProperty('--r', ((cones.children.length % 2 ? 1 : -1) * (4 + (cones.children.length * 3) % 9)) + 'deg');
      c.dataset.pick = i;
      var nm = chips[i].querySelector('.strains__name');
      c.title = 'Tap to take out' + (nm ? ' — ' + nm.textContent.trim() : '');
      cones.appendChild(c);
    }

    chips.forEach(function (c, i) {
      c.dataset.cb = '1';
      chipPaint(c);
      function tap() {
        if (picks.length >= max) {
          if (note) { note.textContent = 'Jar’s full — ' + max + ' rolls'; }
          return;
        }
        picks.push(i);
        drop(i);
        c.classList.remove('is-flash'); void c.offsetWidth; c.classList.add('is-flash');
        clearTimeout(c._flashT);
        c._flashT = setTimeout(function () { c.classList.remove('is-flash'); }, 1100);
        if (navigator.vibrate) { try { navigator.vibrate(8); } catch (e) {} }
        paint();
      }
      c.addEventListener('click', tap);
      c.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tap(); } });
    });

    cones.addEventListener('click', function (e) {
      var c = e.target.closest('.cone');
      if (!c) return;
      var idx = [].indexOf.call(cones.children, c);
      c.classList.add('is-out');
      setTimeout(function () {
        picks.splice(idx, 1);
        if (c.parentNode) c.parentNode.removeChild(c);
        paint();
      }, 160);
      if (navigator.vibrate) { try { navigator.vibrate(8); } catch (e2) {} }
    });

    if (btn) {
      btn.addEventListener('click', function () {
        if (!picks.length) { askForFlavour(strainsBox); return; }
        var counts = {}, missing = false;
        picks.forEach(function (i) {
          var v = chips[i] && chips[i].dataset.var;
          if (v) counts[v] = (counts[v] || 0) + 1; else missing = true;
        });
        var items = Object.keys(counts).map(function (v) { return { id: parseInt(v, 10), quantity: counts[v] }; });
        if (missing || !items.length) {
          if (label) { label.textContent = 'Strain not linked — check variant names'; setTimeout(paint, 2600); }
          return;
        }
        btn.disabled = true;
        if (label) label.textContent = 'Rolling it up…';
        var routes = (window.BigChief && window.BigChief.routes) || {};
        fetch(routes.cartAdd || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
        .then(function (r) { if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Could not add'); }); return r.json(); })
        .then(function () {
          if (label) label.textContent = '✓ In the cart';
          if (navigator.vibrate) { try { navigator.vibrate([10, 40, 14]); } catch (e) {} }
          var C = window.BigChiefCart;
          if (C && C.refresh) return C.refresh().then(function () { if (C.drawer && C.drawer.open) C.drawer.open(); });
        })
        .catch(function (err) { if (label) label.textContent = err.message || 'Could not add'; })
        .then(function () {
          setTimeout(function () {
            btn.disabled = false;
            picks = [];
            while (cones.firstChild) cones.removeChild(cones.firstChild);
            paint();
          }, 2600);
        });
      });
    }

    paint();
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initPrerollJar);
else initPrerollJar();
document.addEventListener('shopify:section:load', initPrerollJar);

/* --------------------------------------------------------------------------
   PASS THE PACK — referral popup. Opens once per visitor after a dwell
   delay, never over the age gate and never on top of the email capture
   (it waits its turn). Any of the three doors — texting a friend, dropping
   a number, following the gram — flips the card to the reward code.
   -------------------------------------------------------------------------- */
function initRefer() {
  var el = document.querySelector('[data-refer]');
  if (!el) return;

  var COOKIE = 'bc_refer';
  var days = parseInt(el.dataset.days, 10) || 14;
  var delay = parseInt(el.dataset.delay, 10) || 50000;
  var posted = !!el.querySelector('.refer__step--code.is-on');

  function suppressed() {
    return document.cookie.split('; ').some(function (c) { return c.indexOf(COOKIE + '=') === 0; });
  }
  function suppress() {
    var exp = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = COOKIE + '=1; expires=' + exp + '; path=/; SameSite=Lax';
  }
  function open() {
    el.hidden = false;
    requestAnimationFrame(function () { el.classList.add('is-open'); });
    suppress();
  }
  function close() {
    el.classList.remove('is-open');
    setTimeout(function () { el.hidden = true; }, 250);
    suppress();
  }
  function reveal() {
    var ask = el.querySelector('[data-refer-step="ask"]');
    var code = el.querySelector('[data-refer-step="code"]');
    if (ask) ask.classList.add('is-done');
    if (code) code.classList.add('is-on');
    suppress();
  }

  el.querySelectorAll('[data-refer-close]').forEach(function (b) { b.addEventListener('click', close); });
  el.querySelectorAll('[data-refer-sent]').forEach(function (a) {
    a.addEventListener('click', function () { setTimeout(reveal, 600); });
  });
  var codeBtn = el.querySelector('[data-refer-code]');
  if (codeBtn) {
    codeBtn.addEventListener('click', function () {
      var note = el.querySelector('[data-refer-code-note]');
      try {
        navigator.clipboard.writeText(codeBtn.dataset.code);
        if (note) note.textContent = 'copied ✓';
      } catch (e) { if (note) note.textContent = codeBtn.dataset.code; }
    });
  }

  /* came back from a successful phone submit: open straight onto the code */
  if (posted) { el.hidden = false; el.classList.add('is-open'); suppress(); return; }

  if (suppressed()) return;

  setTimeout(function tryOpen() {
    var gate = document.querySelector('[data-age-gate]');
    var capture = document.querySelector('[data-capture]');
    var captureOpen = capture && !capture.hidden;
    if (gate || captureOpen) { setTimeout(tryOpen, 6000); return; }
    open();
  }, delay);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initRefer);
else initRefer();
document.addEventListener('shopify:section:load', initRefer);

/* ==========================================================================
   TOUCH BEADS V2 — one finger, one rule.
   On touch screens the bead's CSS :hover reveal and the tap handler fought:
   iOS treats the first tap as hover (card shows), keeps :hover stuck on the
   old bead when you tap the next one, and the second tap navigated — to the
   cartridges page, because the destination setting was never filled in.
   This takes over every bead tap at capture: tap = open THIS card and close
   the others, tap the open bead again (or its GET IT) = go to /pages/duo
   with the pairing. CSS hover reveal is disabled on touch so only one card
   can ever be open.
   ========================================================================== */
(function () {
  var DEST = '/pages/duo';

  var css = document.createElement('style');
  css.textContent =
    '@media (hover: none) {' +
    ' .flavrail .bead:hover .bead__pop { opacity: 0; visibility: hidden; }' +
    ' .flavrail .bead.is-peeking .bead__pop { opacity: 1 !important; visibility: visible !important; }' +
    '}';
  document.head.appendChild(css);

  function act(target) {
    var bead = target.closest && target.closest('.flavrail .bead');
    if (!bead) return false;
    if (target.closest('.bead__buy')) {
      window.location.href = DEST + '?pair=' + (bead.dataset.pair || 0);
      return true;
    }
    /* beads never navigate — tap opens, tap again closes, tapping another
       bead switches. Only the card's GET IT link leaves the page. */
    var was = bead.classList.contains('is-peeking');
    var rail = bead.closest('.flavrail');
    rail.querySelectorAll('.bead.is-peeking').forEach(function (b) { b.classList.remove('is-peeking'); });
    if (!was) {
      bead.classList.add('is-peeking');
      /* On tall phones the card can open below the fold — the tap "did
         nothing". Bring it on screen. */
      var pop = bead.querySelector('.bead__pop');
      if (pop) setTimeout(function () {
        var r = pop.getBoundingClientRect();
        if (r.bottom > window.innerHeight - 8 || r.top < 0) {
          pop.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 60);
    }
    return true;
  }

  /* iOS: elements with hover listeners eat the first tap as "hover", which
     made every other pill feel dead. Acting on touchend — with the synthetic
     mouse events cancelled — makes every tap land first time. A moved finger
     is a scroll, not a tap. */
  var tx = 0, ty = 0;
  document.addEventListener('touchstart', function (e) {
    if (!e.target.closest || !e.target.closest('.flavrail .bead')) return;
    tx = e.touches[0].clientX; ty = e.touches[0].clientY;
  }, { capture: true, passive: true });
  document.addEventListener('touchend', function (e) {
    if (!e.target.closest || !e.target.closest('.flavrail .bead')) return;
    var t = e.changedTouches[0];
    if (Math.abs(t.clientX - tx) > 10 || Math.abs(t.clientY - ty) > 10) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    act(e.target);
  }, { capture: true, passive: false });

  document.addEventListener('click', function (e) {
    if (!e.target.closest || !e.target.closest('.flavrail .bead')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    act(e.target);
  }, true);
})();
